// src/AdminDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import UpgradeModal from './UpgradeModal'; // Your modal component
import BurnSnifferDashboard from './BurnSnifferDashboard';
import UpgradeFailuresDashboard from './UpgradeFailuresDashboard';
import { supabase } from './supabaseClient';
import RNGTestModal from './components/RNGTestModal';
import SupplyPoolsSection from './components/SupplyPoolsSection';
import NadscriptionCampaigns from './components/NadscriptionCampaigns';
import NadscriptionExport from './components/NadscriptionExport';

// Define shared styles
const inputStyle = { padding: '0.5rem', borderRadius: '0.5rem', marginLeft: '0.5rem' };
const buttonStyle = {
  padding: '0.75rem 1.5rem',
  borderRadius: '1rem',
  border: 'none',
  backgroundColor: '#007bff',
  color: '#fff',
  cursor: 'pointer',
  fontSize: '1rem'
};
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || ''
});

function AdminDashboard({ jwtToken: initialJwtToken }) {
  // 1. Admin Authentication State using Supabase email magic link
  const [adminUser, setAdminUser] = useState(null);
  const [jwtToken, setJwtToken] = useState(initialJwtToken);
  const [loadingSession, setLoadingSession] = useState(true);
  const [adminEmail, setAdminEmail] = useState('');
  const [authMessage, setAuthMessage] = useState('');

  // 2. Dashboard Data and UI State
  const [collections, setCollections] = useState([]);
  const [collectionAddress, setCollectionAddress] = useState('');
  const [sampleTokenId, setSampleTokenId] = useState('1');
  const [urnItemId, setUrnItemId] = useState(''); // URN item ID for recipes
  const [selectedChainId, setSelectedChainId] = useState(137); // Polygon=137, Monad=143, ETH=1, Base=8453, ApeChain=33139
  const [upgradeable, setUpgradeable] = useState(false);
  const [upgradeDestination, setUpgradeDestination] = useState('');
  const [upgradeOptions, setUpgradeOptions] = useState([
    { targetCollection: '', quantity: 1, upgradedItemId: '0', upgradedItem: '' }
  ]);
  const [productMintMap, setProductMintMap] = useState([
    { product_id: '', product_title: '', target_collection: '', item_id: '', active: true }
  ]);
  const [existingMappings, setExistingMappings] = useState([]);
  const [dealsForm, setDealsForm] = useState([
    {
      deal_name: '',
      discount_code: '',
      discount_link: '',
      exclusive_product_url: '',
      deal_image: '',
      active: true,

      // NEW 🎉
      logic: 'AND', // or 'OR'
      requiredItems: [
        { collection: '', item_id: '', name: '', url: '' }
      ],
      token_gate_urn_requirements: [{ collection: '', urn_item_id: '', quantity: 1 }]
    }
  ]);
  const [existingDeals, setExistingDeals] = useState([]);
  const [recipesForm, setRecipesForm] = useState([{
    title: '',
    upgrade_destination: 'BURN',  // Set default to BURN
    logic: 'AND',
    recipe_type: 'recipe',
    token_gate_requirements: [],
    token_gate_urn_requirements: [],
    burn_urn_requirements: [], // Initialize burn URN requirements
    token_gate_logic: 'AND',
    // an array of "requires", each one { collection: '', item_id: '' }
    requires: [{ collection: '', item_id: '', quantity: 1 }],
    // an array of "rewards", same shape
    rewards: [{ collection: '', item_id: '', quantity: 1 }],
    limit_per_token_enabled: false, // New field for per-token limits
    limit_per_token: 1, // New field for per-token limit value
    active: true,
    expires_at: '', // New field for expiration
    strict_supply_pool: false // New field for strict supply pool mode
  }]);
  const [existingRecipes, setExistingRecipes] = useState([]);
  const [message, setMessage] = useState('');

  // Section visibility states
  const [showAddCollectionForm, setShowAddCollectionForm] = useState(false);
  const [showCollectionsSection, setShowCollectionsSection] = useState(false);
  const [indexerRunning, setIndexerRunning] = useState(false);
  const [indexerResult, setIndexerResult] = useState(null);
  const [indexerInterval, setIndexerInterval] = useState(5);
  const [indexerLastRun, setIndexerLastRun] = useState(null);
  const [showMintManagerSection, setShowMintManagerSection] = useState(false);
  const [showDealsSection, setShowDealsSection] = useState(false);
  const [showDCLDealsSection, setShowDCLDealsSection] = useState(false);
  const [showRecipesSection, setShowRecipesSection] = useState(false);
  const [showBurnSnifferSection, setShowBurnSnifferSection] = useState(false);
  const [showUpgradeFailuresSection, setShowUpgradeFailuresSection] = useState(false);
  const [showFaucetSection, setShowFaucetSection] = useState(false);
  const [showMaintenanceSection, setShowMaintenanceSection] = useState(false);
  const [showSupplyPoolsSection, setShowSupplyPoolsSection] = useState(false);
  const [showNadscriptionCampaignsSection, setShowNadscriptionCampaignsSection] = useState(false);
  const [showNadscriptionExportSection, setShowNadscriptionExportSection] = useState(false);

  // Supply Pools State
  const [supplyPools, setSupplyPools] = useState([]);
  const [supplyPoolForm, setSupplyPoolForm] = useState({
    name: '',
    description: '',
    total_supply: '',
    items: [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
  });
  const [editingSupplyPool, setEditingSupplyPool] = useState(null);

  const [mintToDeleteIndex, setMintToDeleteIndex] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [mappingToDeleteId, setMappingToDeleteId] = useState(null);
  const [showDeleteExistingModal, setShowDeleteExistingModal] = useState(false);
  const [dealToDeleteId, setDealToDeleteId] = useState(null);
  const [showDeleteDealModal, setShowDeleteDealModal] = useState(false);
  const [showAddRecipeForm, setShowAddRecipeForm] = useState(false);
  const [existingDCLDeals, setExistingDCLDeals] = useState([]);
    const [dclDealsForm, setDclDealsForm] = useState([{
    deal_name: '',
    dealItems: [{ // Array of DCL wearables being sold together
      dcl_collection_address: '',
      dcl_item_id: '',
      dcl_price_mana: '',
      dcl_max_supply: '',
      dcl_beneficiary_address: '0x55CA751383CA801ABEBa78Bbb8b2DE5E2107769A',
      deal_image: ''
    }],
    limit_per_wallet: '',
    daily_limit: '',
    limit_per_token_enabled: false, // New field for per-token limits
    limit_per_token: 1, // New field for per-token limit value
    multi_mint_enabled: false,
    rng_enabled: false,
    rng_pool: [],
    active: true,
    logic: 'AND',
    expires_at: '', // New field for expiration
    strict_supply_pool: false, // New field for strict supply pool mode
    requiredItems: [{ collection: '', item_id: '', name: '', url: '', quantity: 1 }],
    token_gate_urn_requirements: [{ collection: '', urn_item_id: '', quantity: 1 }],
    useCustomCollection: false // New field for UI control
  }]);
  const [dclDealToDeleteId, setDclDealToDeleteId] = useState(null);
  const [showDeleteDCLDealModal, setShowDeleteDCLDealModal] = useState(false);
  const [dclCollections, setDclCollections] = useState([]);

  // FAUCET Configuration State
  const [faucetConfig, setFaucetConfig] = useState({
    enabled: false,
    amount_pol: '0.01',
    frequency_hours: 720, // 30 days default
    token_gate_requirements: [],
    token_gate_logic: 'AND',
    limit_per_token_enabled: true, // Default enabled for faucet (security)
    limit_per_token: 1 // Default 1 per token for faucet
  });

  // Separate state for the current live config (display only)
  const [currentFaucetConfig, setCurrentFaucetConfig] = useState(null);

  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState('We are currently implementing exciting new upgrades to enhance your experience. Please check back soon!');
  const [bypassPin, setBypassPin] = useState('');

  // Mint Launching Mode state
  const [showMintLaunchingSection, setShowMintLaunchingSection] = useState(false);
  const [mintLaunchingMode, setMintLaunchingMode] = useState(false);
  const [mintLaunchDateTime, setMintLaunchDateTime] = useState('');
  const [mintLaunchTimezone, setMintLaunchTimezone] = useState('America/Los_Angeles');
  const [mintLaunchTitle, setMintLaunchTitle] = useState('Mint Launching Shortly');
  const [mintLaunchSubtitle, setMintLaunchSubtitle] = useState('Get ready for an exclusive mint experience');
  const [selectedMintDeals, setSelectedMintDeals] = useState([]);
  const [availableDeals, setAvailableDeals] = useState([]);
  const [selectedMintRecipes, setSelectedMintRecipes] = useState([]);
  const [availableRecipes, setAvailableRecipes] = useState([]);
  const [recipeToDealsMapping, setRecipeToDealsMapping] = useState({}); // Maps recipe ID to array of deal IDs

  // RNG Test Modal state
  const [showRNGTestModal, setShowRNGTestModal] = useState(false);
  const [rngTestItem, setRngTestItem] = useState(null);
  const [rngTestItemType, setRngTestItemType] = useState(null);

  const particleIntervalRef = useRef(null);

  // Particle effect for admin dashboard
  useEffect(() => {
    const createAdminParticle = () => {
      const particle = document.createElement('div');
      particle.className = 'admin-particle';
      particle.style.left = Math.random() * 100 + '%';
      particle.style.animationDuration = (Math.random() * 4 + 6) + 's';
      particle.style.animationDelay = Math.random() * 2 + 's';

      const particlesContainer = document.getElementById('admin-particles');
      if (particlesContainer) {
        particlesContainer.appendChild(particle);

        setTimeout(() => {
          if (particle.parentNode) {
            particle.remove();
          }
        }, 10000);
      }
    };

    // Generate particles for admin dashboard
    const adminInterval = setInterval(createAdminParticle, 500);

    // Create initial particles
    for (let i = 0; i < 8; i++) {
      setTimeout(createAdminParticle, i * 150);
    }

    // Cleanup function
    return () => {
      clearInterval(adminInterval);
    };
  }, []);

  // 3. ADMIN EMAIL AUTHENTICATION FLOW (Using Supabase magic link)
  const handleAdminSignIn = async () => {
    setAuthMessage('');
    const { error } = await supabase.auth.signInWithOtp({ email: adminEmail });
    if (error) {
      setAuthMessage("Error sending magic link: " + error.message);
    } else {
      setAuthMessage("Magic link sent to " + adminEmail + ". Check your email to complete sign in.");
    }
  };

  // Listen to auth state changes from Supabase.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setAdminUser(session.user);
        setJwtToken(session.access_token);

      }
    });
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        setAdminUser(session.user);
        setJwtToken(session.access_token);
      }
      setLoadingSession(false);
    })();
    return () => {
      if (subscription && typeof subscription.unsubscribe === 'function') {
        subscription.unsubscribe();
      }
    };
  }, []);

  // 4. Data Fetching Functions
  const fetchCollections = async () => {
    try {
      const res = await api.get('/api/admin', {
        params: { action: 'collections' }
      });
      const data = res.data?.collections;
      if (!Array.isArray(data)) {
        console.error('fetchCollections: unexpected payload', res.data);
        setCollections([]);
      } else {
        setCollections(data);
      }
    } catch (err) {
      console.error('Error fetching collections:', err);
      setCollections([]);
    }
  };

  const fetchSupplyPools = async () => {
    try {
      console.log('🔍 Fetching supply pools...');
      const res = await api.get('/api/admin', {
        params: { action: 'get-supply-pools' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      console.log('✅ Supply pools response:', res.data);
      const data = res.data?.supply_pools;
      if (!Array.isArray(data)) {
        console.error('fetchSupplyPools: unexpected payload', res.data);
        setSupplyPools([]);
      } else {
        console.log(`📦 Setting ${data.length} supply pools in state`);
        setSupplyPools(data);
      }
    } catch (err) {
      console.error('❌ fetchSupplyPools error:', err);
      if (err.response?.status === 400 || err.response?.status === 500) {
        console.warn('⚠️ Supply pools tables may not exist yet. Run the database migrations first: add_shared_supply_pools.sql and add_supply_pools_rls.sql');
      }
      setSupplyPools([]);
    }
  };

  const fetchMintMappings = async () => {
    try {
      const res = await api.get('/api/admin', {
        params: { action: 'product-mint-map' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setExistingMappings(res.data.mappings || []);
    } catch (e) { console.error(e); }
  };

  const fetchExistingDeals = async () => {
    try {
      const { data } = await api.get('/api/deals', {
        headers: { Authorization: `Bearer ${jwtToken}` }
      })
      setExistingDeals(data.deals)
    } catch (err) {
      console.error("Error fetching existing deals:", err)
      setExistingDeals([])
    }
  }

  const fetchExistingDCLDeals = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'dcl-deals' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      })
      setExistingDCLDeals(data.deals || [])
    } catch (err) {
      console.error("Error fetching existing DCL deals:", err)
      setExistingDCLDeals([])
    }
  }

  const fetchDCLCollections = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'dcl-collections' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      })
      setDclCollections(data.collections || [])
    } catch (err) {
      console.error("Error fetching DCL collections:", err)
      setDclCollections([])
    }
  }

  const fetchFaucetConfig = async () => {
    try {
      const { data } = await api.get('/api/faucet', {
        params: { action: 'config' }
      })
      // Set current config for display
      setCurrentFaucetConfig(data)
      // Always update form with latest data on initial load/refresh
      setFaucetConfig(data)
    } catch (err) {
      console.error("Error fetching faucet config:", err)
      const defaultConfig = {
        enabled: false,
        amount_pol: '0.01',
        frequency_hours: 720,
        token_gate_requirements: [],
        token_gate_logic: 'AND'
      }
      setCurrentFaucetConfig(null)
      setFaucetConfig(defaultConfig)
    }
  }

  // Fetch indexer settings when collections section is opened
  useEffect(() => {
    if (showCollectionsSection && jwtToken) {
      fetchIndexerSettings();
    }
  }, [showCollectionsSection]);

  useEffect(() => {
    if (adminUser) {
      fetchCollections();
      fetchMintMappings();
      fetchExistingDeals();
      fetchExistingDCLDeals();
      fetchDCLCollections();
      fetchRecipes();
      fetchFaucetConfig();
      fetchSupplyPools();
      checkMaintenanceStatus();
      checkMintLaunchingStatus();
      fetchAvailableDeals();
      fetchAvailableRecipes();
    }
  }, [adminUser]);

  // 5. Handler Functions for Upgrade Options and Product Mint Mapping
  const addUpgradeOption = () => {
    setUpgradeOptions([...upgradeOptions, { targetCollection: '', quantity: 1, upgradedItemId: '', upgradedItem: '' }]);
  };

  // Remove an upgrade option at a given index.
  const removeUpgradeOption = (index) => {
    setUpgradeOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpgradeOptionChange = (index, field, value) => {
    const newOptions = [...upgradeOptions];
    newOptions[index][field] = value;
    setUpgradeOptions(newOptions);
  };

  const handleRemoveMintEntry = (index) => {
    setMintToDeleteIndex(index);
    setShowDeleteModal(true);
  };

  const confirmRemoveMintEntry = () => {
    setProductMintMap((prev) => prev.filter((_, i) => i !== mintToDeleteIndex));
    setShowDeleteModal(false);
    setMintToDeleteIndex(null);
  };

  const handleProductMintChange = (index, field, value) => {
    const newMap = [...productMintMap];
    
    // Handle collection item selection by ID (like other dropdowns)
    if (field === 'collection_item_selection') {
      if (value) {
        // Find the selected collection item by ID
        const selectedItem = collections.find(c => c.id === parseInt(value));
        
        if (selectedItem) {
          // Set both collection_item_id and target_collection address
          newMap[index].collection_item_id = selectedItem.id;
          newMap[index].target_collection = selectedItem.collection_address;
          
          // DON'T auto-populate item_id - that field might be for specific token ID or other purposes
          // URN validation will be handled by the backend using collection_item_id
        }
      } else {
        // Clear collection-related fields if no collection selected
        newMap[index].collection_item_id = '';
        newMap[index].target_collection = '';
        // Don't auto-clear item_id - user might want to set it manually
      }
    } else {
      // For other fields, just set the value normally
      newMap[index][field] = value;
    }
    
    setProductMintMap(newMap);
  };

  const addProductMintEntry = () => {
    setProductMintMap([...productMintMap, { product_id: '', product_title: '', target_collection: '', item_id: '', active: true }]);
  };

  const handleSubmitMintMap = async () => {
    try {
      await api.post('/api/admin', {
        entries: productMintMap
      }, {
        params: { action: 'add-product-mint-map' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMessage('Mint mappings saved successfully');
      setProductMintMap([{ product_id: '', product_title: '', target_collection: '', item_id: '', active: true }]);
      fetchMintMappings();
    } catch (error) {
      console.error('Error saving mint mappings:', error);
      setMessage('Error saving mint mappings: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDealChange = (dealIndex, field, value) => {
    const newDeals = [...dealsForm];
    newDeals[dealIndex][field] = value;
    setDealsForm(newDeals);
  };

  const addDealEntry = () => {
    setDealsForm([...dealsForm, {
      deal_name: '',
      discount_code: '',
      discount_link: '',
      exclusive_product_url: '',
      deal_image: '',
      active: true,
      logic: 'AND',
      requiredItems: [{ collection: '', item_id: '', name: '', url: '' }],
      token_gate_urn_requirements: [{ collection: '', urn_item_id: '', quantity: 1 }]
    }]);
  };

  const removeDealEntry = (dealIndex) => {
    setDealsForm(prev => prev.filter((_, i) => i !== dealIndex));
  };

  const handleSubmitDeals = async () => {
    try {
      // Transform the dealsForm to match what your backend expects
      const payload = dealsForm.map(deal => ({
        deal_name: deal.deal_name,
        discount_code: deal.discount_code,
        discount_link: deal.discount_link,
        exclusive_product_url: deal.exclusive_product_url,
        deal_image: deal.deal_image,
        active: deal.active,
        logic: deal.logic,
        required_items: deal.requiredItems,
        token_gate_urn_requirements: deal.token_gate_urn_requirements || [],
        // Legacy fallback if backend still expects these flat fields:
        collection: deal.requiredItems[0]?.collection || '',
        required_item_id: deal.requiredItems[0]?.item_id || ''
      }));

      await api.post('/api/deals', {
        entries: payload
      }, {
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      setMessage('Deals saved successfully');
      setDealsForm([{
        deal_name: '',
        discount_code: '',
        discount_link: '',
        exclusive_product_url: '',
        deal_image: '',
        active: true,
        logic: 'AND',
        requiredItems: [{ collection: '', item_id: '', name: '', url: '' }],
        token_gate_urn_requirements: [{ collection: '', urn_item_id: '', quantity: 1 }]
      }]);
      fetchExistingDeals();
    } catch (error) {
      console.error('Error saving deals:', error);
      setMessage('Error saving deals: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteExistingDeal = async (id) => {
    try {
      await api.delete('/api/deals', {
        params: { id },
        headers: { Authorization: `Bearer ${jwtToken}` }
      })
      setMessage('Deal deleted successfully')
      // Refresh both deal lists since the deal could be in either
      fetchExistingDeals()
      fetchExistingDCLDeals()
    } catch (error) {
      console.error('Error deleting deal:', error)
      setMessage('Error deleting deal: ' + (error.response?.data?.error || error.message))
    }
  }

  const handleLogicChange = (dealIndex, newLogic) => {
    const newDeals = [...dealsForm];
    newDeals[dealIndex].logic = newLogic;
    setDealsForm(newDeals);
  };

  const addRequiredItem = (dealIndex) => {
    const newDeals = [...dealsForm];
    newDeals[dealIndex].requiredItems.push({
      collection: '',
      item_id: '',
      name: '',
      url: ''
    });
    setDealsForm(newDeals);
  };

  const removeRequiredItem = (dealIndex, itemIndex) => {
    const newDeals = [...dealsForm];
    newDeals[dealIndex].requiredItems = newDeals[dealIndex].requiredItems.filter(
      (_, i) => i !== itemIndex
    );
    setDealsForm(newDeals);
  };

  const handleRequiredItemChange = (dealIndex, itemIndex, field, value) => {
    const newDeals = [...dealsForm];
    
    // Handle collection item selection by ID (like other dropdowns)
    if (field === 'collection_item_selection') {
      if (value) {
        // Find the selected collection item by ID
        const selectedItem = collections.find(c => c.id === parseInt(value));
        
        if (selectedItem) {
          // Set both collection_item_id and collection address
          newDeals[dealIndex].requiredItems[itemIndex].collection_item_id = selectedItem.id;
          newDeals[dealIndex].requiredItems[itemIndex].collection = selectedItem.collection_address;
          newDeals[dealIndex].requiredItems[itemIndex].name = selectedItem.title;
          
          // If this item has a URN item ID, populate the URN requirements
          if (selectedItem.urn_item_id !== null) {
            // Ensure token_gate_urn_requirements array exists and has the right length
            if (!newDeals[dealIndex].token_gate_urn_requirements) {
              newDeals[dealIndex].token_gate_urn_requirements = [];
            }
            while (newDeals[dealIndex].token_gate_urn_requirements.length <= itemIndex) {
              newDeals[dealIndex].token_gate_urn_requirements.push({
                collection: '',
                urn_item_id: '',
                quantity: 1
              });
            }
            
            // Populate the corresponding URN requirement
            newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
              collection: selectedItem.collection_address,
              urn_item_id: selectedItem.urn_item_id,
              quantity: newDeals[dealIndex].requiredItems[itemIndex].quantity || 1
            };
          } else {
            // Clear URN requirement if item doesn't have URN
            if (newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
              newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
                collection: '',
                urn_item_id: '',
                quantity: 1
              };
            }
          }
        }
      } else {
        // Clear collection-related fields if no collection selected
        newDeals[dealIndex].requiredItems[itemIndex].collection_item_id = '';
        newDeals[dealIndex].requiredItems[itemIndex].collection = '';
        // Don't auto-clear name - user might want to set it manually
        if (newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
          newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
            collection: '',
            urn_item_id: '',
            quantity: 1
          };
        }
      }
    } else {
      // For other fields, just set the value normally
      newDeals[dealIndex].requiredItems[itemIndex][field] = value;
      
      // Sync quantity changes between both arrays
      if (field === 'quantity' && newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
        newDeals[dealIndex].token_gate_urn_requirements[itemIndex].quantity = value;
      }
    }
    
    setDealsForm(newDeals);
  };

  // URN Requirements handlers for deals
  const addDealUrnRequirement = (dealIndex) => {
    const newDeals = [...dealsForm];
    if (!newDeals[dealIndex].token_gate_urn_requirements) {
      newDeals[dealIndex].token_gate_urn_requirements = [];
    }
    newDeals[dealIndex].token_gate_urn_requirements.push({
      collection: '',
      urn_item_id: '',
      quantity: 1
    });
    setDealsForm(newDeals);
  };

  const removeDealUrnRequirement = (dealIndex, urnIndex) => {
    const newDeals = [...dealsForm];
    newDeals[dealIndex].token_gate_urn_requirements = newDeals[dealIndex].token_gate_urn_requirements.filter(
      (_, i) => i !== urnIndex
    );
    setDealsForm(newDeals);
  };

  // DCL Deals handlers
  const handleDCLDealChange = (dealIndex, field, value) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex][field] = value;
    
    // Auto-populate deal image when collection address is selected/changed
    if (field === 'dcl_collection_address' && value) {
      const selectedCollection = dclCollections.find(
        col => col.collection_address.toLowerCase() === value.toLowerCase()
      );
      if (selectedCollection) {
        newDeals[dealIndex]['deal_image'] = selectedCollection.image || '';
        // Also auto-populate deal name if it's empty
        if (!newDeals[dealIndex]['deal_name']) {
          newDeals[dealIndex]['deal_name'] = `${selectedCollection.title} Wearable`;
        }
      }
    }
    
    setDclDealsForm(newDeals);
  };

  const addDCLDealEntry = () => {
    setDclDealsForm(f => f.concat({
      deal_name: '',
      deal_type: 'dcl_wearable',
      dealItems: [],
      rng_enabled: false,
      rng_pool: [],
      approved_collections: [],
      token_gate_requirements: [],
      token_gate_logic: 'AND'
    }));
  };

  const removeDCLDealEntry = (dealIndex) => {
    setDclDealsForm(prev => prev.filter((_, i) => i !== dealIndex));
  };

  const handleDCLLogicChange = (dealIndex, newLogic) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].logic = newLogic;
    setDclDealsForm(newDeals);
  };

  const addDCLRequiredItem = (dealIndex) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].requiredItems.push({
      collection: '',
      item_id: '',
      name: '',
      url: '',
      quantity: 1
    });
    // Also add corresponding URN requirement
    if (!newDeals[dealIndex].token_gate_urn_requirements) {
      newDeals[dealIndex].token_gate_urn_requirements = [];
    }
    newDeals[dealIndex].token_gate_urn_requirements.push({
      collection: '',
      urn_item_id: '',
      quantity: 1
    });
    setDclDealsForm(newDeals);
  };

  const removeDCLRequiredItem = (dealIndex, itemIndex) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].requiredItems = newDeals[dealIndex].requiredItems.filter(
      (_, i) => i !== itemIndex
    );
    // Also remove corresponding URN requirement
    if (newDeals[dealIndex].token_gate_urn_requirements) {
      newDeals[dealIndex].token_gate_urn_requirements = newDeals[dealIndex].token_gate_urn_requirements.filter(
        (_, i) => i !== itemIndex
      );
    }
    setDclDealsForm(newDeals);
  };

  const handleDCLRequiredItemChange = (dealIndex, itemIndex, field, value) => {
    const newDeals = [...dclDealsForm];
    
    // Handle collection item selection by ID (like other dropdowns)
    if (field === 'collection_item_selection') {
      if (value) {
        // Find the selected collection item by ID
        const selectedItem = collections.find(c => c.id === parseInt(value));
        
        if (selectedItem) {
          // Set both collection_item_id and collection address
          newDeals[dealIndex].requiredItems[itemIndex].collection_item_id = selectedItem.id;
          newDeals[dealIndex].requiredItems[itemIndex].collection = selectedItem.collection_address;
          newDeals[dealIndex].requiredItems[itemIndex].name = selectedItem.title;
          
          // If this item has a URN item ID, populate the URN requirements
          if (selectedItem.urn_item_id !== null) {
            // Ensure token_gate_urn_requirements array exists and has the right length
            if (!newDeals[dealIndex].token_gate_urn_requirements) {
              newDeals[dealIndex].token_gate_urn_requirements = [];
            }
            while (newDeals[dealIndex].token_gate_urn_requirements.length <= itemIndex) {
              newDeals[dealIndex].token_gate_urn_requirements.push({
                collection: '',
                urn_item_id: '',
                quantity: 1
              });
            }
            
            // Populate the corresponding URN requirement
            newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
              collection: selectedItem.collection_address,
              urn_item_id: selectedItem.urn_item_id,
              quantity: newDeals[dealIndex].requiredItems[itemIndex].quantity || 1
            };
          } else {
            // Clear URN requirement if item doesn't have URN
            if (newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
              newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
                collection: '',
                urn_item_id: '',
                quantity: 1
              };
            }
          }
        }
      } else {
        // Clear collection-related fields if no collection selected
        newDeals[dealIndex].requiredItems[itemIndex].collection_item_id = '';
        newDeals[dealIndex].requiredItems[itemIndex].collection = '';
        newDeals[dealIndex].requiredItems[itemIndex].name = '';
        if (newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
          newDeals[dealIndex].token_gate_urn_requirements[itemIndex] = {
            collection: '',
            urn_item_id: '',
            quantity: 1
          };
        }
      }
    } else {
      // For other fields, just set the value normally
      newDeals[dealIndex].requiredItems[itemIndex][field] = value;
      
      // Sync quantity changes between both arrays
      if (field === 'quantity' && newDeals[dealIndex].token_gate_urn_requirements && newDeals[dealIndex].token_gate_urn_requirements[itemIndex]) {
        newDeals[dealIndex].token_gate_urn_requirements[itemIndex].quantity = value;
      }
    }
    
    setDclDealsForm(newDeals);
  };

  // DCL Deal Items handlers (the actual wearables being sold)
  const addDCLDealItem = (dealIndex) => {
    const newDeals = [...dclDealsForm];
    if (!newDeals[dealIndex].dealItems) {
      newDeals[dealIndex].dealItems = [];
    }
    newDeals[dealIndex].dealItems.push({
      dcl_collection_address: '',
      dcl_item_id: '',
      dcl_price_mana: '',
      dcl_max_supply: '',
      dcl_beneficiary_address: '0x55CA751383CA801ABEBa78Bbb8b2DE5E2107769A',
      deal_image: '',
      chance: 100 // Initialize with 100% chance
    });
    setDclDealsForm(newDeals);
  };

  const removeDCLDealItem = (dealIndex, itemIndex) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].dealItems = newDeals[dealIndex].dealItems.filter(
      (_, i) => i !== itemIndex
    );
    setDclDealsForm(newDeals);
  };

  const handleDCLDealItemChange = (dealIndex, itemIndex, field, value) => {
    const newDeals = [...dclDealsForm];
    
    // Special handling for chance field to ensure it's a number
    if (field === 'chance') {
      // Accept empty string for controlled input, otherwise store as number
      newDeals[dealIndex].dealItems[itemIndex][field] = value === '' ? '' : Number(value);
    } else {
      newDeals[dealIndex].dealItems[itemIndex][field] = value;
    }
    
    setDclDealsForm(newDeals);
  };

  // Handle approved collection selection for DCL deals
  const handleDCLApprovedCollectionChange = (dealIndex, collectionAddress) => {
    const newDeals = [...dclDealsForm];
    const selectedCollection = collections.find(
      col => col.collection_address.toLowerCase() === collectionAddress.toLowerCase()
    );

    if (selectedCollection) {
      // Auto-populate the required items with the selected collection
      newDeals[dealIndex].requiredItems = [{
        collection: selectedCollection.collection_address,
        item_id: '',
        name: selectedCollection.title,
        url: '' // Will be auto-generated when item_id is added
      }];
    } else {
      // Clear if no selection
      newDeals[dealIndex].requiredItems = [{ collection: '', item_id: '', name: '', url: '' }];
    }

    setDclDealsForm(newDeals);
  };

  const handleSubmitDCLDeals = async () => {
    try {
      // Process the form data before submission
      const processedEntries = dclDealsForm.map(entry => {
        const processedEntry = { ...entry };
        
        // If RNG is enabled, extract chances from dealItems into rng_pool
        if (entry.rng_enabled && entry.dealItems && entry.dealItems.length > 0) {
          // Store the chances before removing them from dealItems
          processedEntry.rng_pool = entry.dealItems.map(item => Number(item.chance));
          
          // Remove chance field from dealItems
          processedEntry.dealItems = entry.dealItems.map(({ chance, ...item }) => item);
        }
        
        return processedEntry;
      });

      await api.post('/api/admin', {
        entries: processedEntries
      }, {
        params: { action: 'dcl-deals' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      setMessage('DCL Wearable deals saved successfully');
      setDclDealsForm([{
        deal_name: '',
        dealItems: [{
          dcl_collection_address: '',
          dcl_item_id: '',
          dcl_price_mana: '',
          dcl_max_supply: '',
          dcl_beneficiary_address: '0x55CA751383CA801ABEBa78Bbb8b2DE5E2107769A',
          deal_image: ''
        }],
        limit_per_wallet: '',
        daily_limit: '',
        limit_per_token_enabled: false,
        limit_per_token: 1,
        multi_mint_enabled: false,
        rng_enabled: false,
        rng_pool: [],
        active: true,
        logic: 'AND',
        requiredItems: [{ collection: '', item_id: '', name: '', url: '', quantity: 1 }],
        token_gate_urn_requirements: [{ collection: '', urn_item_id: '', quantity: 1 }],
        useCustomCollection: false
      }]);
      fetchExistingDCLDeals();
    } catch (error) {
      console.error('Error saving DCL deals:', error);
      setMessage('Error saving DCL deals: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteExistingDCLDeal = async (id) => {
    try {
      await api.delete('/api/deals', {
        params: { id },
        headers: { Authorization: `Bearer ${jwtToken}` }
      })
      setMessage('DCL deal deleted successfully')
      // Refresh both deal lists since the deal could be in either
      fetchExistingDeals()
      fetchExistingDCLDeals()
    } catch (error) {
      console.error('Error deleting DCL deal:', error)
      setMessage('Error deleting DCL deal: ' + (error.response?.data?.error || error.message))
    }
  }

  // Fetch indexer settings
  const fetchIndexerSettings = async () => {
    try {
      const response = await api.get('/api/admin', {
        params: { action: 'indexer-settings' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setIndexerInterval(response.data.intervalMinutes ?? 5);
      setIndexerLastRun(response.data.lastRun);
    } catch (error) {
      console.error('Error fetching indexer settings:', error);
    }
  };

  // Update indexer interval
  const handleUpdateIndexerInterval = async (newInterval) => {
    try {
      console.log('Updating indexer interval to:', newInterval);
      const response = await api.post('/api/admin?action=indexer-settings', 
        { intervalMinutes: Number(newInterval) },
        { headers: { Authorization: `Bearer ${jwtToken}` }}
      );
      console.log('Update response:', response.data);
      setIndexerInterval(newInterval);
    } catch (error) {
      console.error('Error updating indexer interval:', error.response?.data || error);
    }
  };

  // Trigger NFT indexer to scan for collection ownership
  const handleTriggerIndexer = async () => {
    setIndexerRunning(true);
    setIndexerResult(null);
    try {
      const response = await api.get('/api/admin', {
        params: { action: 'trigger-indexer' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setIndexerResult(response.data);
      console.log('Indexer result:', response.data);
    } catch (error) {
      console.error('Error triggering indexer:', error);
      setIndexerResult({ error: error.response?.data?.error || error.message });
    } finally {
      setIndexerRunning(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!collectionAddress || !sampleTokenId) {
      setMessage('Please provide both collection address and token ID.');
      return;
    }

    if (upgradeable && (!upgradeDestination || upgradeOptions.length === 0)) {
      setMessage('Upgradeable collections require upgrade destination and at least one upgrade option.');
      return;
    }

    const collectionData = {
      collectionAddress,
      sampleTokenId,
      urnItemId: urnItemId.trim() || null, // Include URN item ID if provided
      chainId: selectedChainId, // Include chain ID for multi-chain support
      upgradeable,
      upgradeDestination: upgradeable ? upgradeDestination : null,
      upgradeOptions: upgradeable ? upgradeOptions : null
    };

    try {
      const response = await api.post('/api/admin', {
        collectionAddress,
        sampleTokenId,
        urnItemId: urnItemId.trim() || null, // Include URN item ID if provided
        chainId: selectedChainId, // Include chain ID for multi-chain support
        upgradeable,
        upgradeDestination: upgradeable ? upgradeDestination : null,
        upgradeOptions: upgradeable ? upgradeOptions : null
      }, {
        params: { action: 'add-collection' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMessage('Collection item added successfully!');
      setCollectionAddress('');
      setSampleTokenId('1');
      setUrnItemId(''); // Reset URN item ID
      setSelectedChainId(137); // Reset chain to Polygon
      setUpgradeable(false);
      setUpgradeDestination('');
      setUpgradeOptions([{ targetCollection: '', quantity: 1, upgradedItemId: '0', upgradedItem: '' }]);
      fetchCollections();
    } catch (error) {
      console.error('Error adding collection:', error);
      setMessage('Error adding collection item: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) {
      return;
    }

    try {
      await api.delete('/api/admin', {
        params: { action: 'delete-collection', id: collectionId },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMessage('Collection deleted successfully!');
      fetchCollections();
    } catch (error) {
      console.error('Error deleting collection:', error);
      setMessage('Error deleting collection: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteExistingMapping = async (mappingId) => {
    try {
      await api.delete('/api/admin', {
        params: { action: 'delete-product-mint-map', id: mappingId },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMessage('Mapping deleted successfully');
      fetchMintMappings();
    } catch (error) {
      console.error('Error deleting mapping:', error);
      setMessage('Error deleting mapping: ' + (error.response?.data?.error || error.message));
    }
  };

  // Supply Pool Handlers
  const handleSaveSupplyPool = async (poolForm, editingId) => {
    try {
      const apiAction = editingId ? 'update-supply-pool' : 'create-supply-pool';
      const method = editingId ? 'PATCH' : 'POST';
      
      const poolData = {
        name: poolForm.name,
        description: poolForm.description,
        // total_supply is auto-calculated from items on backend
        items: poolForm.items.filter(item => item.item_name || item.collection_address || item.urn_item_id).map(item => ({
          ...item,
          quantity: parseInt(item.quantity) || 1
        })),
        created_by: adminUser?.email || 'admin'
      };
      
      if (editingId) {
        poolData.id = editingId;
      }

      const res = await api({
        method,
        url: '/api/admin',
        params: { action: apiAction },
        data: poolData,
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      if (res.data.success) {
        setMessage(res.data.message || 'Supply pool saved successfully');
        setTimeout(() => setMessage(''), 3000);
        
        // Reset form
        setSupplyPoolForm({
          name: '',
          description: '',
          total_supply: '',
          items: [{ item_name: '', collection_address: '', urn_item_id: '', image_url: '', quantity: 1 }]
        });
        setEditingSupplyPool(null);
        
        // Refetch supply pools
        fetchSupplyPools();
        
        // Refetch deals and recipes
        fetchExistingDCLDeals();
        fetchRecipes();
      }
    } catch (error) {
      console.error('Error saving supply pool:', error);
      setMessage('Error saving supply pool: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const handleDeleteSupplyPool = async (poolId) => {
    if (!window.confirm('Are you sure you want to delete this supply pool? It must not be linked to any active deals or recipes.')) {
      return;
    }

    try {
      const res = await api.delete('/api/admin', {
        params: { action: 'delete-supply-pool' },
        data: { id: poolId },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      if (res.data.success) {
        setMessage(res.data.message || 'Supply pool deleted successfully');
        setTimeout(() => setMessage(''), 3000);
        
        // Refetch supply pools
        fetchSupplyPools();
        
        // Refetch deals and recipes
        fetchExistingDCLDeals();
        fetchRecipes();
      }
    } catch (error) {
      console.error('Error deleting supply pool:', error);
      const errorMsg = error.response?.data?.error || error.message;
      setMessage('Error deleting supply pool: ' + errorMsg);
      setTimeout(() => setMessage(''), 5000);
      
      // If there are linked deals/recipes, show them
      if (error.response?.data?.linked_deals || error.response?.data?.linked_recipes) {
        const linked = [];
        if (error.response.data.linked_deals?.length > 0) {
          linked.push(`${error.response.data.linked_deals.length} deal(s)`);
        }
        if (error.response.data.linked_recipes?.length > 0) {
          linked.push(`${error.response.data.linked_recipes.length} recipe(s)`);
        }
        alert(`Cannot delete: This supply pool is linked to ${linked.join(' and ')}. Please unlink them first.`);
      }
    }
  };

  const addRecipeEntry = () =>
    setRecipesForm(f => f.concat({
      title: '',
      upgrade_destination: 'BURN',  // Set default to BURN
      logic: 'AND',
      recipe_type: 'recipe',
      token_gate_requirements: [],
      token_gate_urn_requirements: [], // New field for URN validation
      burn_urn_requirements: [], // Initialize burn URN requirements
      token_gate_logic: 'AND',
      requires: [{ collection: '', item_id: '', quantity: 1 }],
      rewards: [{ collection: '', item_id: '', quantity: 1 }],
      limit_per_token_enabled: false, // New field for per-token limits
      limit_per_token: 1, // New field for per-token limit value
      active: true
    }));

  const handleRecipeChange = (idx, field, val) =>
    setRecipesForm(f => {
      const copy = [...f];
      copy[idx][field] = val;
      
      // Initialize token gate requirements when recipe type changes to token-gated or free_mint
      if (field === 'recipe_type' && (val === 'token_gated' || val === 'free_mint')) {
        if (!copy[idx].token_gate_requirements || copy[idx].token_gate_requirements.length === 0) {
          copy[idx].token_gate_requirements = [{ collection: '', item_id: '', quantity: 1, marketplace_link: '' }];
        }
        if (!copy[idx].token_gate_urn_requirements || copy[idx].token_gate_urn_requirements.length === 0) {
          copy[idx].token_gate_urn_requirements = [{ collection: '', urn_item_id: '', quantity: 1 }];
        }
      }
      
      return copy;
    });

  const addRequire = (recipeIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].requires.push({ collection: '', item_id: '', quantity: 1 });
      return copy;
    });
  };

  const removeRequire = (recipeIdx, rIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].requires = copy[recipeIdx].requires.filter((_, i) => i !== rIdx);
      return copy;
    });
  };


  const handleRequireChange = (idx, rIdx, field, val) =>
    setRecipesForm(f => {
      const copy = [...f];
      
      // Handle collection item selection by ID (like token gates and rewards)
      if (field === 'collection_item_selection') {
        if (val) {
          // Find the selected collection item by ID
          const selectedItem = collections.find(c => c.id === parseInt(val));
          
          if (selectedItem) {
            // Set collection address in requires
            copy[idx].requires[rIdx].collection = selectedItem.collection_address;
            // Store the selected collection item ID temporarily for dropdown state
            copy[idx].requires[rIdx]._selected_item_id = selectedItem.id;
            
            // If this item has a URN item ID, also populate burn_urn_requirements automatically
            if (selectedItem.urn_item_id !== null) {
              // Ensure burn_urn_requirements array exists
              if (!copy[idx].burn_urn_requirements) {
                copy[idx].burn_urn_requirements = [];
              }
              
              // Remove any existing burn requirement for this exact collection + urn_item_id combination
              copy[idx].burn_urn_requirements = copy[idx].burn_urn_requirements.filter(
                req => !(req.collection === selectedItem.collection_address && req.urn_item_id === selectedItem.urn_item_id)
              );
              
              // Add new burn URN requirement
              copy[idx].burn_urn_requirements.push({
                collection: selectedItem.collection_address,
                urn_item_id: selectedItem.urn_item_id,
                quantity: copy[idx].requires[rIdx].quantity || 1
              });
            }
            
            // DON'T touch item_id - that's for specific token lockdown (golden rule)
          }
        } else {
          // Clear fields if no collection selected
          copy[idx].requires[rIdx].collection = '';
          copy[idx].requires[rIdx]._selected_item_id = '';
          // DON'T clear item_id - that's user input for token locking
        }
      } else {
        // For other fields, just set the value normally
        copy[idx].requires[rIdx][field] = val;
      }
      
      return copy;
    });

  const addReward = (recipeIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].rewards.push({ collection: '', item_id: '', quantity: 1 });
      return copy;
    });
  };

  const removeReward = (recipeIdx, rwIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].rewards = copy[recipeIdx].rewards.filter((_, i) => i !== rwIdx);
      return copy;
    });
  };

  const handleRewardChange = (idx, rwIdx, field, val) =>
    setRecipesForm(f => {
      const copy = [...f];
      copy[idx].rewards[rwIdx][field] = val;
      return copy;
    });

  const handleRecipeLogicChange = (recipeIndex, newLogic) => {
    const newRecipes = [...recipesForm];
    newRecipes[recipeIndex].logic = newLogic;
    setRecipesForm(newRecipes);
  };

  const fetchRecipes = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'recipes' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setExistingRecipes(data.recipes || []);
    } catch (err) {
      console.error("Error fetching recipes:", err);
      setExistingRecipes([]);
    }
  };

  const handleSubmitRecipes = async () => {
    try {
      await api.post('/api/admin', {
        entries: recipesForm
      }, {
        params: { action: 'recipes' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMessage('Recipes saved successfully');
      setRecipesForm([{
        title: '',
        upgrade_destination: 'BURN',
        logic: 'AND',
        recipe_type: 'recipe',
        token_gate_requirements: [],
        token_gate_urn_requirements: [], // New field for URN validation
        burn_urn_requirements: [], // Initialize burn URN requirements
        token_gate_logic: 'AND',
        requires: [{ collection: '', item_id: '', quantity: 1 }],
        rewards: [{ collection: '', item_id: '', quantity: 1 }],
        active: true
      }]);
      await fetchRecipes();
    } catch (error) {
      console.error('Error saving recipes:', error);
      setMessage('Error saving recipes: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteRecipe = async (id) => {
    try {
      await api.delete('/api/admin', {
        params: { action: 'delete-recipe', id },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      await fetchRecipes();
      setMessage('Recipe deleted successfully');
    } catch (error) {
      console.error('Error deleting recipe:', error);
      setMessage('Error deleting recipe: ' + (error.response?.data?.error || error.message));
    }
  };

  const removeRecipeEntry = (idx) => {
    setRecipesForm(f => f.filter((_, i) => i !== idx));
  };

  // Token gate management functions
  const addTokenGate = (recipeIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].token_gate_requirements.push({ collection: '', item_id: '', quantity: 1, marketplace_link: '' });
      copy[recipeIdx].token_gate_urn_requirements.push({ collection: '', urn_item_id: '', quantity: 1 });
      return copy;
    });
  };

  const removeTokenGate = (recipeIdx, gateIdx) => {
    setRecipesForm(f => {
      const copy = [...f];
      copy[recipeIdx].token_gate_requirements = copy[recipeIdx].token_gate_requirements.filter((_, i) => i !== gateIdx);
      copy[recipeIdx].token_gate_urn_requirements = copy[recipeIdx].token_gate_urn_requirements.filter((_, i) => i !== gateIdx);
      return copy;
    });
  };

  const handleTokenGateChange = (recipeIdx, gateIdx, field, val) => {
    setRecipesForm(f => {
      const copy = [...f];
      
      // When collection item is selected by ID
      if (field === 'collection_item_selection') {
        if (val) {
          // Find the selected collection item by ID
          const selectedItem = collections.find(c => c.id === parseInt(val));
          
          if (selectedItem) {
            // Set the collection address in token_gate_requirements
            copy[recipeIdx].token_gate_requirements[gateIdx].collection = selectedItem.collection_address;
            
            // If this item has a URN item ID, populate the URN requirements
            if (selectedItem.urn_item_id !== null) {
              // Ensure token_gate_urn_requirements array exists and has the right length
              if (!copy[recipeIdx].token_gate_urn_requirements) {
                copy[recipeIdx].token_gate_urn_requirements = [];
              }
              while (copy[recipeIdx].token_gate_urn_requirements.length <= gateIdx) {
                copy[recipeIdx].token_gate_urn_requirements.push({
                  collection: '',
                  urn_item_id: '',
                  quantity: 1
                });
              }
              
              // Populate the corresponding URN requirement
              copy[recipeIdx].token_gate_urn_requirements[gateIdx] = {
                collection: selectedItem.collection_address,
                urn_item_id: selectedItem.urn_item_id,
                quantity: copy[recipeIdx].token_gate_requirements[gateIdx].quantity || 1
              };
            } else {
              // Clear URN requirement if item doesn't have URN
              if (copy[recipeIdx].token_gate_urn_requirements && copy[recipeIdx].token_gate_urn_requirements[gateIdx]) {
                copy[recipeIdx].token_gate_urn_requirements[gateIdx] = {
                  collection: '',
                  urn_item_id: '',
                  quantity: 1
                };
              }
            }
          }
        } else {
          // Clear all related fields if no collection selected
          copy[recipeIdx].token_gate_requirements[gateIdx].collection = '';
          if (copy[recipeIdx].token_gate_urn_requirements && copy[recipeIdx].token_gate_urn_requirements[gateIdx]) {
            copy[recipeIdx].token_gate_urn_requirements[gateIdx] = {
              collection: '',
              urn_item_id: '',
              quantity: 1
            };
          }
        }
      } else {
        // For other fields, just set the value normally
        copy[recipeIdx].token_gate_requirements[gateIdx][field] = val;
        
        // Sync quantity changes between both arrays
        if (field === 'quantity' && copy[recipeIdx].token_gate_urn_requirements && copy[recipeIdx].token_gate_urn_requirements[gateIdx]) {
          copy[recipeIdx].token_gate_urn_requirements[gateIdx].quantity = val;
        }
      }
      
      return copy;
    });
  };

  // FAUCET Configuration Handlers
  const handleFaucetConfigChange = (field, value) => {
    setFaucetConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addFaucetTokenGate = () => {
    setFaucetConfig(prev => ({
      ...prev,
      token_gate_requirements: [
        ...prev.token_gate_requirements,
        { collection: '', item_id: '', quantity: 1, marketplace_link: '' }
      ]
    }));
  };

  const removeFaucetTokenGate = (gateIndex) => {
    setFaucetConfig(prev => ({
      ...prev,
      token_gate_requirements: prev.token_gate_requirements.filter((_, i) => i !== gateIndex)
    }));
  };

  const handleFaucetTokenGateChange = (gateIndex, field, value) => {
    setFaucetConfig(prev => ({
      ...prev,
      token_gate_requirements: prev.token_gate_requirements.map((gate, i) => 
        i === gateIndex ? { ...gate, [field]: value } : gate
      )
    }));
  };

  const handleSubmitFaucetConfig = async () => {
    try {
      const { data } = await api.post('/api/faucet', faucetConfig, {
        params: { action: 'admin-update' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      
      if (data.success) {
        setMessage('Faucet configuration updated successfully!');
        fetchFaucetConfig(); // Refresh the config
      } else {
        setMessage('Error updating faucet configuration');
      }
    } catch (err) {
      console.error('Error updating faucet config:', err);
      setMessage('Error updating faucet configuration: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleToggleFaucetEnabled = async () => {
    const newState = !faucetConfig.enabled;
    const action = newState ? 'enable' : 'disable';
    
    if (!window.confirm(`Are you sure you want to ${action} the faucet?`)) {
      return;
    }

    try {
      setMessage(`${action === 'enable' ? 'Enabling' : 'Disabling'} faucet...`);
      
      // Update the existing config with new enabled state (toggle mode)
      const updatedConfig = { 
        ...currentFaucetConfig, 
        enabled: newState,
        isToggle: true  // Flag to tell backend this is just a toggle, not full config creation
      };
      
      const { data } = await api.post('/api/faucet', updatedConfig, {
        params: { action: 'admin-update' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      if (data.success) {
        setMessage(`✅ Faucet ${action}d successfully!`);
        setCurrentFaucetConfig(data.config); // Update current config immediately
        fetchFaucetConfig(); // Refresh the config
      } else {
        setMessage(`❌ Failed to ${action} faucet`);
      }
    } catch (err) {
      console.error(`Error ${action}ing faucet:`, err);
      setMessage(`❌ Error ${action}ing faucet: ` + (err.response?.data?.error || err.message));
    }
  };

  const handleDeleteFaucetConfig = async () => {
    if (!window.confirm('Are you sure you want to completely remove the faucet configuration? This will delete all settings and you\'ll need to recreate everything.')) {
      return;
    }

    try {
      setMessage('Removing faucet configuration...');
      
      const { data } = await api.delete('/api/faucet', {
        params: { action: 'admin-delete' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });

      if (data.success) {
        setMessage('✅ Faucet configuration removed successfully!');
        setCurrentFaucetConfig(null); // Clear current config immediately
        fetchFaucetConfig(); // Refresh the config
      } else {
        setMessage('❌ Failed to remove faucet configuration');
      }
    } catch (err) {
      console.error('Error removing faucet config:', err);
      setMessage('❌ Error removing faucet config: ' + (err.response?.data?.error || err.message));
    }
  };

  // Add RNG pool handlers
  const addRNGPoolItem = (dealIndex) => {
    const newDeals = [...dclDealsForm];
    if (!newDeals[dealIndex].rng_pool) {
      newDeals[dealIndex].rng_pool = [];
    }
    newDeals[dealIndex].rng_pool.push({
      dcl_collection_address: '',
      dcl_item_id: '',
      dcl_price_mana: '',
      chance: 100
    });
    setDclDealsForm(newDeals);
  };

  const removeRNGPoolItem = (dealIndex, itemIndex) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].rng_pool = newDeals[dealIndex].rng_pool.filter(
      (_, i) => i !== itemIndex
    );
    setDclDealsForm(newDeals);
  };

  const handleRNGPoolItemChange = (dealIndex, itemIndex, field, value) => {
    const newDeals = [...dclDealsForm];
    newDeals[dealIndex].rng_pool[itemIndex][field] = value;
    setDclDealsForm(newDeals);
  };

  // Add maintenance mode toggle handler
  const handleMaintenanceToggle = async (enabled) => {
    try {
      const response = await api.post('/api/admin', {
        enabled,
        message: maintenanceMessage,
        bypass_pin: bypassPin
      }, {
        params: { action: 'maintenance' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      
      if (response.data.success) {
        setMaintenanceMode(enabled);
        setMessage(`Maintenance mode ${enabled ? 'enabled' : 'disabled'} successfully`);
      }
    } catch (error) {
      console.error('Error toggling maintenance mode:', error);
      setMessage('Failed to update maintenance mode');
    }
  };

  // Add maintenance message update handler
  const handleMaintenanceMessageChange = async (newMessage) => {
    try {
      const response = await api.post('/api/admin', {
        enabled: maintenanceMode,
        message: newMessage,
        bypass_pin: bypassPin
      }, {
        params: { action: 'maintenance' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      
      if (response.data.success) {
        setMaintenanceMessage(newMessage);
        setMessage('Maintenance message updated successfully');
      }
    } catch (error) {
      console.error('Error updating maintenance message:', error);
      setMessage('Failed to update maintenance message');
    }
  };

  // Add maintenance mode check
  const checkMaintenanceStatus = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'maintenance' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      setMaintenanceMode(data.enabled);
      setMaintenanceMessage(data.message);
      setBypassPin(data.bypass_pin || '');
    } catch (error) {
      console.error('Error checking maintenance status:', error);
      setMessage('Failed to fetch maintenance status');
    }
  };

  // Mint Launching Mode handlers
  const checkMintLaunchingStatus = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'mint-launching' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      
      setMintLaunchingMode(data.enabled);
      setMintLaunchDateTime(data.launch_datetime ? new Date(data.launch_datetime).toISOString().slice(0, 16) : '');
      setMintLaunchTimezone(data.launch_timezone || 'America/Los_Angeles');
      setMintLaunchTitle(data.title || 'Mint Launching Shortly');
      setMintLaunchSubtitle(data.subtitle || 'Get ready for an exclusive mint experience');
      setSelectedMintDeals(data.selected_deal_ids || []);
      setSelectedMintRecipes(data.selected_recipe_ids || []);
      setRecipeToDealsMapping(data.recipe_to_deals_mapping || {});
    } catch (error) {
      console.error('Error checking mint launching status:', error);
      setMessage('Failed to fetch mint launching status');
    }
  };

  const handleSaveMintLaunchingConfig = async () => {
    try {
      // Validate launch datetime if enabling
      if (mintLaunchingMode && (!mintLaunchDateTime || new Date(mintLaunchDateTime) <= new Date())) {
        setMessage('Please set a valid launch date and time in the future');
        return;
      }


      const response = await api.post('/api/admin', {
        enabled: mintLaunchingMode,
        launch_datetime: mintLaunchDateTime,
        launch_timezone: mintLaunchTimezone,
        selected_deal_ids: selectedMintDeals,
        selected_recipe_ids: selectedMintRecipes,
        recipe_to_deals_mapping: recipeToDealsMapping,
        title: mintLaunchTitle,
        subtitle: mintLaunchSubtitle
      }, {
        params: { action: 'mint-launching' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      
      
      if (response.data.success) {
        setMessage(`Mint launching configuration saved successfully - ${mintLaunchingMode ? 'ENABLED' : 'DISABLED'}`);
        // Refresh the config to verify it was saved
        await checkMintLaunchingStatus();
      }
    } catch (error) {
      console.error('Error saving mint launching config:', error);
      setMessage('Failed to save mint launching configuration');
    }
  };

  const fetchAvailableDeals = async () => {
    try {
      const { data } = await api.get('/api/deals');
      setAvailableDeals(data.deals || []);
    } catch (error) {
      console.error('Error fetching available deals:', error);
      setMessage('Failed to fetch available deals');
    }
  };

  const fetchAvailableRecipes = async () => {
    try {
      const { data } = await api.get('/api/admin', {
        params: { action: 'recipes' },
        headers: { Authorization: `Bearer ${jwtToken}` }
      });
      // Show ALL recipes - let admin choose which ones to use as free mints
      setAvailableRecipes(data.recipes || []);
    } catch (error) {
      console.error('Error fetching available recipes:', error);
      setMessage('Failed to fetch available recipes');
    }
  };

  const handleDealSelection = (dealId, isSelected) => {
    if (isSelected) {
      setSelectedMintDeals(prev => [...prev, dealId]);
    } else {
      setSelectedMintDeals(prev => prev.filter(id => id !== dealId));
      // Clean up any recipe mappings that reference this deal
      setRecipeToDealsMapping(prev => {
        const newMapping = { ...prev };
        Object.keys(newMapping).forEach(recipeId => {
          newMapping[recipeId] = newMapping[recipeId].filter(id => id !== dealId);
          // Remove empty arrays
          if (newMapping[recipeId].length === 0) {
            delete newMapping[recipeId];
          }
        });
        return newMapping;
      });
    }
  };

  const handleRecipeSelection = (recipeId, isSelected) => {
    if (isSelected) {
      setSelectedMintRecipes(prev => [...prev, recipeId]);
    } else {
      setSelectedMintRecipes(prev => prev.filter(id => id !== recipeId));
      // Clean up the mapping when recipe is deselected
      setRecipeToDealsMapping(prev => {
        const newMapping = { ...prev };
        delete newMapping[recipeId];
        return newMapping;
      });
    }
  };

  // 7. Render the Component
  if (loadingSession) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000)',
        backgroundSize: '400% 400%',
        animation: 'gradientShift 15s ease infinite',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        fontSize: '1.5rem',
        fontWeight: '600'
      }}>
        Loading Admin Dashboard...
      </div>
    );
  }

  // If no admin is signed in, show the admin sign-in panel.
  if (!adminUser) {
    return (
      <>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
            
            .admin-signin-page {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              margin: 0;
              padding: 0;
              font-family: 'Inter', sans-serif;
              background: linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000);
              background-size: 400% 400%;
              animation: gradientShift 15s ease infinite;
              color: white;
              overflow-x: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
            }
            
            @keyframes gradientShift {
              0% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
            
            .admin-signin-container {
              text-align: center;
              max-width: 500px;
              padding: 3rem;
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(15px);
              border: 1px solid rgba(255, 255, 255, 0.2);
              border-radius: 20px;
              box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            }
            
            .admin-signin-title {
              font-size: 2.5rem;
              font-weight: 800;
              margin-bottom: 1rem;
              background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
              background-size: 400% 400%;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: rainbowFlow 3s ease-in-out infinite;
            }
            
            @keyframes rainbowFlow {
              0%, 100% { background-position: 0% 50%; }
              50% { background-position: 100% 50%; }
            }
            
            .admin-signin-subtitle {
              font-size: 1.2rem;
              margin-bottom: 2rem;
              opacity: 0.9;
            }
            
            .admin-signin-form {
              display: flex;
              flex-direction: column;
              gap: 1rem;
              align-items: center;
            }
            
            .admin-signin-input {
              padding: 1rem;
              border-radius: 10px;
              border: 1px solid rgba(255, 255, 255, 0.3);
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              color: white;
              font-size: 1rem;
              width: 300px;
              max-width: 100%;
              box-sizing: border-box;
            }
            
            .admin-signin-input::placeholder {
              color: rgba(255, 255, 255, 0.7);
            }
            
            .admin-signin-input:focus {
              outline: none;
              border-color: rgba(255, 255, 255, 0.5);
              box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
            }
            
            .admin-signin-button {
              padding: 1rem 2rem;
              background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
              background-size: 400% 400%;
              border: none;
              border-radius: 10px;
              color: white;
              font-size: 1rem;
              font-weight: 600;
              cursor: pointer;
              animation: rainbowFlow 3s ease-in-out infinite;
              transition: transform 0.3s ease, box-shadow 0.3s ease;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            }
            
            .admin-signin-button:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
            }
            
            .admin-signin-message {
              margin-top: 1rem;
              padding: 1rem;
              border-radius: 10px;
              background: rgba(255, 255, 255, 0.1);
              backdrop-filter: blur(10px);
              border: 1px solid rgba(255, 255, 255, 0.2);
            }
          `}
        </style>
        <div className="admin-signin-page">
          <div className="admin-signin-container">
            <h1 className="admin-signin-title">Admin Portal</h1>
            <p className="admin-signin-subtitle">Please Sign In to Continue</p>
            <div className="admin-signin-form">
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="Enter admin email"
                className="admin-signin-input"
              />
              <button onClick={handleAdminSignIn} className="admin-signin-button">
                Send Magic Link
              </button>
            </div>
            {authMessage && (
              <p className="admin-signin-message">{authMessage}</p>
            )}
          </div>
        </div>
      </>
    );
  }

  // Once the admin is authenticated, render the dashboard content.
  return (
    <div>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;600;700;800&display=swap');
          
          .adminDashboardContainer {
            min-height: 100vh;
            background: linear-gradient(-45deg, #000000, #1a1a1a, #2d2d2d, #000000);
            background-size: 400% 400%;
            animation: gradientShift 15s ease infinite;
            color: white;
            font-family: 'Inter', sans-serif;
            position: relative;
            overflow-x: hidden;
            padding: 2rem;
          }
          
          @keyframes gradientShift {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          .admin-particles {
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            pointer-events: none;
            z-index: 1;
          }
          
          .admin-particle {
            position: absolute;
            width: 3px;
            height: 3px;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2);
            border-radius: 50%;
            animation: adminFloat 8s linear infinite;
            opacity: 0.6;
          }
          
          @keyframes adminFloat {
            0% {
              transform: translateY(100vh) rotate(0deg);
              opacity: 0;
            }
            10% {
              opacity: 0.6;
            }
            90% {
              opacity: 0.6;
            }
            100% {
              transform: translateY(-100px) rotate(360deg);
              opacity: 0;
            }
          }
          
          .admin-section {
            position: relative;
            z-index: 2;
            margin: 2rem 0;
          }
          
          .admin-header {
            text-align: center;
            font-size: 2.5rem;
            font-weight: 800;
            margin: 2rem 0;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: rainbowFlow 3s ease-in-out infinite;
            text-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
          }
          
          .admin-subheader {
            font-size: 1.8rem;
            font-weight: 700;
            margin: 1.5rem 0;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            animation: rainbowFlow 3s ease-in-out infinite;
            cursor: pointer;
            transition: transform 0.2s ease;
            text-align: center;
          }
          
          .admin-subheader:hover {
            transform: scale(1.05);
          }
          
          @keyframes rainbowFlow {
            0%, 100% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
          }
          
          .glassmorphism-card {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border: 1px solid rgba(255, 255, 255, 0.2);
            border-radius: 20px;
            padding: 2rem;
            margin: 1rem 0;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            transition: all 0.3s ease;
          }
          
          .glassmorphism-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 48px rgba(0, 0, 0, 0.4);
            border-color: rgba(255, 255, 255, 0.3);
          }
          
          .admin-form {
            background: rgba(255, 255, 255, 0.05);
            backdrop-filter: blur(20px);
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 15px;
            padding: 2rem;
            margin: 1rem auto;
            max-width: 800px;
            text-align: center;
          }
          
          .admin-input {
            padding: 0.8rem;
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            color: white;
            margin-left: 0.5rem;
            transition: all 0.3s ease;
          }
          
          .admin-input::placeholder {
            color: rgba(255, 255, 255, 0.7);
          }
          
          .admin-input:focus {
            outline: none;
            border-color: rgba(255, 255, 255, 0.5);
            box-shadow: 0 0 10px rgba(255, 255, 255, 0.2);
            background: rgba(255, 255, 255, 0.15);
          }
          
          .admin-select {
            padding: 0.8rem;
            border-radius: 10px;
            border: 1px solid rgba(255, 255, 255, 0.3);
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            color: white;
            margin-left: 0.5rem;
            max-width:500px;
          }
          
          .admin-select option {
            background: #333;
            color: white;
          }
          
          .rainbowBtn {
            display: inline-block;
            padding: 0.8rem 1.5rem;
            background: linear-gradient(45deg, #ff0000, #ff8c00, #ffd700, #32cd32, #00bfff, #8a2be2, #ff1493);
            background-size: 400% 400%;
            border: none;
            border-radius: 10px;
            color: white;
            font-size: 0.9rem;
            font-weight: 600;
            text-decoration: none;
            text-transform: uppercase;
            letter-spacing: 1px;
            transition: all 0.3s ease;
            animation: rainbowFlow 3s ease-in-out infinite;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            cursor: pointer;
            margin: 0.25rem;
          }
          
          .rainbowBtn:hover {
            transform: translateY(-2px) scale(1.05);
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
          }
          
          .tile-container {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            justify-content: center;
            padding: 1rem;
          }
          
          .tile {
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            border-radius: 15px;
            padding: 1.5rem;
            transition: all 0.3s ease;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            position: relative;
            overflow: hidden;
            max-width: 350px;
            width: 100%;
            word-wrap: break-word;
            overflow-wrap: break-word;
          }
          
          .tile::before {
            content: '';
            position: absolute;
            top: 0;
            left: -100%;
            width: 100%;
            height: 100%;
            background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
            transition: left 0.5s ease;
          }
          
          .tile:hover::before {
            left: 100%;
          }
          
          .tile:hover {
            transform: translateY(-10px) scale(1.02);
            box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4);
          }
          
          .admin-form-group {
            margin-bottom: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: 0.5rem;
          }
          
          .admin-form-group label {
            font-weight: 600;
            color: white;
            min-width: 140px;
            text-align: right;
          }
          
          .admin-upgrade-option {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1rem;
            margin-bottom: 1rem;
            border-radius: 10px;
          }
          
          .admin-two-column {
            display: flex;
            gap: 2rem;
            align-items: flex-start;
          }
          
          .admin-column {
            flex: 1;
          }
          
          .admin-existing-list {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 10px;
            padding: 1rem;
          }
          
          .admin-existing-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0.5rem 0;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          }
          
          .admin-existing-item:last-child {
            border-bottom: none;
          }
          
          .admin-delete-btn {
            background: none;
            border: none;
            color: #e74c3c;
            cursor: pointer;
            font-size: 1.2rem;
            transition: all 0.2s ease;
          }
          
          .admin-delete-btn:hover {
            color: #c0392b;
            transform: scale(1.2);
          }
          
          .admin-message {
            text-align: center;
            padding: 1rem;
            background: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(10px);
            border-radius: 10px;
            margin: 1rem 0;
            border: 1px solid rgba(255, 255, 255, 0.2);
          }
          
          .admin-recipe-container {
            background: rgba(255, 255, 255, 0.05);
            border: 1px solid rgba(255, 255, 255, 0.1);
            padding: 1rem;
            margin: 1rem 0;
            border-radius: 10px;
          }
          
          .admin-recipe-items {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.5rem;
            margin-bottom: 1rem;
          }
          
          .admin-recipe-item {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 0.25rem;
            padding: 0.5rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 8px;
            min-width: 200px;
          }
          
          .admin-required-item {
            margin: 0.5rem 0;
            padding: 0.5rem;
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.05);
          }
          
          .admin-required-item h4 {
            margin: 0 0 0.5rem 0;
            color: white;
          }
          
          .admin-required-item-inputs {
            display: flex;
            gap: 0.5rem;
            flex-wrap: wrap;
            align-items: center;
          }
          
          @media (max-width: 768px) {
            .admin-two-column {
              flex-direction: column;
            }
            
            .adminDashboardContainer {
              padding: 1rem;
            }
            
            .admin-header {
              font-size: 2rem;
            }
            
            .admin-subheader {
              font-size: 1.5rem;
            }
          }
        `}
      </style>

      <div className="adminDashboardContainer">
        <div className="admin-particles" id="admin-particles"></div>

        <div className="admin-section">
          <h1 className="admin-header">⚙️ Admin Dashboard</h1>
        </div>

        {/* Toggle for Add Collection Form */}
        <div className="admin-section">
          <h2 
            className="admin-subheader"
            onClick={() => setShowAddCollectionForm(!showAddCollectionForm)}
          >
            {showAddCollectionForm ? "▼" : "+"} Add a Collection Item
          </h2>
          {showAddCollectionForm && (
            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>Blockchain:</label>
                <select
                  value={selectedChainId}
                  onChange={(e) => setSelectedChainId(parseInt(e.target.value))}
                  className="admin-select"
                  style={{ 
                    backgroundColor: selectedChainId === 143 ? '#1a1a2e' : 
                                     selectedChainId === 1 ? '#1a1a3e' : 
                                     selectedChainId === 8453 ? '#0052ff20' :
                                     selectedChainId === 33139 ? '#0038ff' : '#2d1a4a', 
                    color: '#fff' 
                  }}
                >
                  <option value={137}>🟣 Polygon (Chain ID: 137)</option>
                  <option value={143}>🟢 Monad (Chain ID: 143)</option>
                  <option value={1}>🔷 Ethereum (Chain ID: 1)</option>
                  <option value={8453}>🔵 Base (Chain ID: 8453)</option>
                  <option value={33139}>🦧 ApeChain (Chain ID: 33139)</option>
                </select>
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  💡 Select the blockchain where this NFT collection exists
                </small>
              </div>
              <div className="admin-form-group">
                <label>Collection Address:</label>
                <input
                  type="text"
                  value={collectionAddress}
                  onChange={(e) => setCollectionAddress(e.target.value)}
                  placeholder="0x..."
                  required
                  className="admin-input"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  💡 {selectedChainId === 143 ? 'Enter any Monad NFT collection address' :
                      selectedChainId === 1 ? 'Enter any Ethereum NFT collection address' :
                      selectedChainId === 8453 ? 'Enter any Base NFT collection address' :
                      selectedChainId === 33139 ? 'Enter any ApeChain NFT collection address' :
                      'Enter any Polygon NFT collection address'}
                </small>
              </div>
              <div className="admin-form-group">
                <label>Token ID:</label>
                <input
                  type="text"
                  value={sampleTokenId}
                  onChange={(e) => setSampleTokenId(e.target.value)}
                  placeholder="Blockchain token ID (e.g., 1, 105312291...)"
                  required
                  className="admin-input"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  💡 The actual blockchain token ID for this collection item
                </small>
              </div>
              <div className="admin-form-group">
                <label>URN Item ID:</label>
                <input
                  type="text"
                  value={urnItemId}
                  onChange={(e) => setUrnItemId(e.target.value)}
                  placeholder="URN item ID for recipes (0, 1, 2...) - optional"
                  className="admin-input"
                />
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  💡 Optional: URN item ID used in recipes. Only needed if different from token ID.
                </small>
              </div>
              <div className="admin-form-group">
                <label>Upgradeable:</label>
                <input
                  type="checkbox"
                  checked={upgradeable}
                  onChange={(e) => setUpgradeable(e.target.checked)}
                  style={{ marginLeft: '0.5rem' }}
                />
              </div>
              {upgradeable && (
                <>
                  <div className="admin-form-group">
                    <label>Upgrade Recipient:</label>
                    <input
                      type="text"
                      value={upgradeDestination}
                      onChange={(e) => setUpgradeDestination(e.target.value)}
                      placeholder="Enter address or type BURN"
                      required
                      className="admin-input"
                    />
                  </div>
                  <h3 style={{ color: 'white', textAlign: 'center', margin: '1rem 0' }}>Upgrade Options</h3>
                  {upgradeOptions.map((option, index) => (
                    <div key={index} className="admin-upgrade-option">
                      <div className="admin-form-group">
                        <label>Target Collection:</label>
                        <select
                          value={option.targetCollection}
                          onChange={(e) => {
                            handleUpgradeOptionChange(index, 'targetCollection', e.target.value);
                            const col = collections.find(c => c.collection_address === e.target.value);
                            if (col) {
                              handleUpgradeOptionChange(index, 'upgradedItem', col.title);
                            }
                          }}
                          className="admin-select"
                        >
                          <option value="">– pick collection –</option>
                          {collections.map(c => (
                            <option key={c.id} value={c.collection_address}>
                              {c.title}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label>Quantity:</label>
                        <input
                          type="number"
                          min="1"
                          value={option.quantity}
                          onChange={(e) => handleUpgradeOptionChange(index, 'quantity', e.target.value)}
                          required
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label>Upgraded Item ID:</label>
                        <input
                          type="text"
                          value={option.upgradedItemId}
                          onChange={(e) => handleUpgradeOptionChange(index, 'upgradedItemId', e.target.value)}
                          placeholder="Item ID"
                          required
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label>Upgraded Item Name:</label>
                        <input
                          type="text"
                          value={option.upgradedItem}
                          onChange={(e) => handleUpgradeOptionChange(index, 'upgradedItem', e.target.value)}
                          placeholder="Name/Description"
                          className="admin-input"
                        />
                      </div>
                      {upgradeOptions.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeUpgradeOption(index)}
                          style={{ 
                            padding: '0.5rem 1rem', 
                            borderRadius: '0.75rem', 
                            border: 'none', 
                            backgroundColor: '#e74c3c', 
                            color: '#fff', 
                            cursor: 'pointer', 
                            fontSize: '0.9rem', 
                            marginTop: '0.5rem' 
                          }}
                        >
                          Remove Option
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="rainbowBtn" onClick={addUpgradeOption}>
                    Add Upgrade Option
                  </button>
                </>
              )}
              <button type="submit" className="rainbowBtn" style={{ marginLeft: '1rem' }}>
                Add Collection
              </button>
            </form>
          )}
        </div>

        {/* Toggle for Existing Collections */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowCollectionsSection(!showCollectionsSection)}
          >
            {showCollectionsSection ? "▼" : "►"} 👕 Collection Items
          </h2>
          {showCollectionsSection && (
            <>
              {/* Indexer Controls */}
              <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#1a1a2e', borderRadius: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                  <button 
                    onClick={handleTriggerIndexer}
                    disabled={indexerRunning}
                    className="rainbowBtn"
                  >
                    {indexerRunning ? '🔄 Indexing...' : '🔍 Run NFT Indexer'}
                  </button>
                  
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <label style={{ color: '#888', fontSize: '0.9em' }}>Auto-run every:</label>
                    <select
                      value={indexerInterval}
                      onChange={(e) => handleUpdateIndexerInterval(parseFloat(e.target.value))}
                      style={{ 
                        padding: '0.3rem 0.5rem', 
                        borderRadius: '4px', 
                        backgroundColor: '#2a2a3e',
                        color: '#fff',
                        border: '1px solid #444'
                      }}
                    >
                      <option value={0}>Continuous (no delay)</option>
                      <option value={0.5}>30 sec</option>
                      <option value={1}>1 min</option>
                      <option value={2}>2 min</option>
                      <option value={5}>5 min</option>
                      <option value={10}>10 min</option>
                      <option value={15}>15 min</option>
                      <option value={30}>30 min</option>
                      <option value={60}>1 hour</option>
                      <option value={120}>2 hours</option>
                      <option value={360}>6 hours</option>
                      <option value={1440}>24 hours</option>
                    </select>
                  </div>
                </div>
                
                <div style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.85em' }}>
                  Scans blockchain for NFT ownership. Run manually after adding new collections.
                  {indexerLastRun && (
                    <span style={{ marginLeft: '0.5rem' }}>
                      Last run: {new Date(indexerLastRun).toLocaleString()}
                    </span>
                  )}
                </div>
                
                {indexerResult && (
                  <div style={{ 
                    marginTop: '0.5rem', 
                    padding: '0.5rem', 
                    backgroundColor: indexerResult.error ? '#3a1a1a' : '#1a3a1a',
                    borderRadius: '4px',
                    fontSize: '0.85em'
                  }}>
                    {indexerResult.error ? (
                      <span style={{ color: '#ff6b6b' }}>❌ {indexerResult.error}</span>
                    ) : indexerResult.skipped ? (
                      <span style={{ color: '#ffbb6b' }}>⏭️ {indexerResult.reason}</span>
                    ) : (
                      <span style={{ color: '#6bff6b' }}>
                        ✅ Indexed {indexerResult.result?.transfers || 0} transfers across {indexerResult.result?.collections || 0} collections
                        {indexerResult.result?.rounds && ` (${indexerResult.result.rounds} rounds)`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            <div className="tile-container">
              {Array.isArray(collections) && collections.length > 0 ? (
                collections.map((collection, index) => (
                  <div
                    key={collection.id || index}
                    className="tile"
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h2>{collection.title}</h2>
                      <button
                        onClick={() => handleDelete(collection.id)}
                        className="admin-delete-btn"
                        title="Delete collection item"
                      >
                        🗑️
                      </button>
                    </div>
                    <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <strong>Chain:</strong>{' '}
                      <span style={{ 
                        backgroundColor: collection.chain_id === 143 ? '#00ff88' : 
                                         collection.chain_id === 1 ? '#627eea' :
                                         collection.chain_id === 8453 ? '#0052ff' :
                                         collection.chain_id === 33139 ? '#0038ff' : '#9b59b6',
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '0.85em'
                      }}>
                        {collection.chain_id === 143 ? '🟢 Monad' : 
                         collection.chain_id === 1 ? '🔷 Ethereum' :
                         collection.chain_id === 8453 ? '🔵 Base' :
                         collection.chain_id === 33139 ? '🦧 ApeChain' : '🟣 Polygon'}
                      </span>
                    </p>
                    <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <strong>Address:</strong> {collection.collection_address}
                    </p>
                    <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                      <strong>Token ID:</strong> {collection.token_id}
                    </p>
                    <img src={collection.image} alt={collection.title} style={{ maxWidth: '200px', margin: '0 auto' }} />
                    <p><strong>Upgradeable:</strong> {collection.upgradeable ? 'Yes' : 'No'}</p>
                    {collection.upgradeable && (
                      <p style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                        <strong>Upgrade Options:</strong> {JSON.stringify(collection.upgrade_options)}
                      </p>
                    )}
                    <div className="attributes-grid">
                      {Array.isArray(collection.attributes) ? collection.attributes.map((attr, idx) => (
                        <div key={idx}>
                          <strong>{attr.trait_type}:</strong> {attr.value}
                        </div>
                      )) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="glassmorphism-card">
                  <p style={{ textAlign: 'center', margin: 0 }}>No collection items added yet.</p>
                </div>
              )}
            </div>
            </>
          )}
        </div>

        {/* Toggle for 🧵 Phygital Mint Manager */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowMintManagerSection(!showMintManagerSection)}
          >
            {showMintManagerSection ? "▼" : "►"} 🧵 Phygital Mints
          </h2>
          {showMintManagerSection && (
            <div className="admin-two-column">
              {/* Left Column – New Mint Mapping Form */}
              <div className="admin-column">
                <div className="admin-form">
                  {productMintMap.map((entry, index) => (
                    <div key={index} className="admin-upgrade-option">
                      <div className="admin-form-group">
                        <label>Product ID:</label>
                        <input
                          type="number"
                          value={entry.product_id}
                          onChange={(e) =>
                            handleProductMintChange(index, 'product_id', e.target.value)
                          }
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label>Product Title:</label>
                        <input
                          type="text"
                          value={entry.product_title}
                          onChange={(e) =>
                            handleProductMintChange(index, 'product_title', e.target.value)
                          }
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label>Target Collection:</label>
                        <select
                          value={
                            entry.collection_item_id || 
                            (entry.target_collection ? collections.find(c => c.collection_address === entry.target_collection)?.id || "" : "")
                          }
                          onChange={(e) =>
                            handleProductMintChange(index, 'collection_item_selection', e.target.value)
                          }
                          className="admin-select"
                        >
                          <option value="">– pick collection –</option>
                          {collections.map(c => (
                            <option key={c.id} value={c.id}>
                              {c.title} {c.urn_item_id !== null ? `(URN ID: ${c.urn_item_id})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="admin-form-group">
                        <label>Item ID:</label>
                        <input
                          type="text"
                          value={entry.item_id}
                          onChange={(e) =>
                            handleProductMintChange(index, 'item_id', e.target.value)
                          }
                          className="admin-input"
                        />
                      </div>
                      <div className="admin-form-group">
                        <label>Active:</label>
                        <input
                          type="checkbox"
                          checked={entry.active}
                          onChange={(e) =>
                            handleProductMintChange(index, 'active', e.target.checked)
                          }
                          style={{ marginLeft: '0.5rem' }}
                        />
                      </div>
                      {productMintMap.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveMintEntry(index)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          Remove Mapping
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" onClick={addProductMintEntry} className="rainbowBtn">
                    Add Mint Mapping
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitMintMap}
                    className="rainbowBtn"
                    style={{ marginLeft: '1rem' }}
                  >
                    Save Mint Mappings
                  </button>
                </div>
              </div>

              {/* Right Column – Existing Mappings List */}
              <div className="admin-column">
                <div className="admin-existing-list">
                  <h4 style={{ color: 'white' }}>Existing Mappings</h4>
                  {(!Array.isArray(existingMappings) || existingMappings.length === 0) ? (
                    <p>No mappings yet.</p>
                  ) : (
                    existingMappings.map((entry, idx) => (
                      <div
                        key={entry.id || idx}
                        className="admin-existing-item"
                      >
                        <span>
                          {entry.product_title} (ID: {entry.product_id})
                        </span>
                        <button
                          title="Delete"
                          className="admin-delete-btn"
                          onClick={() => handleDeleteExistingMapping(entry.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle for 🏷️ NFT Deals Manager */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowDealsSection(!showDealsSection)}
          >
            {showDealsSection ? '▼' : '►'} 🏷️ NFT Deals
          </h2>
          {showDealsSection && (
            <div className="admin-two-column">
              {/* Left column: new deals form */}
              <div className="admin-column">
                <div className="admin-form">
                  {dealsForm.map((entry, index) => (
                    <div key={index} className="admin-upgrade-option">
                      {/* Deal name */}
                      <div className="admin-form-group">
                        <label>Deal name:</label>
                        <input
                          type="text"
                          value={entry.deal_name}
                          onChange={e =>
                            handleDealChange(index, 'deal_name', e.target.value)
                          }
                          className="admin-input"
                        />
                      </div>

                      {/* AND / OR logic */}
                      <div className="admin-form-group">
                        <label>Require all items?</label>
                        <select
                          value={entry.logic}
                          onChange={e => handleLogicChange(index, e.target.value)}
                          className="admin-select"
                        >
                          <option value="AND">Yes (AND)</option>
                          <option value="OR">No (OR)</option>
                        </select>
                      </div>

                      {/* Required Items list */}
                      {entry.requiredItems.map((ri, riIndex) => (
                        <div
                          key={riIndex}
                          className="admin-required-item"
                        >
                          <h4>Required Item {riIndex + 1}</h4>
                          <div className="admin-required-item-inputs">
                            <select
                              value={
                                ri.collection_item_id || 
                                (ri.collection ? collections.find(c => c.collection_address === ri.collection)?.id || "" : "")
                              }
                              onChange={(e) =>
                                handleRequiredItemChange(
                                  index,
                                  riIndex,
                                  'collection_item_selection',
                                  e.target.value
                                )
                              }
                              className="admin-select"
                            >
                              <option value="">– pick collection –</option>
                              {collections.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.title} {c.urn_item_id !== null ? `(URN ID: ${c.urn_item_id})` : ''}
                                </option>
                              ))}
                            </select>
                            <input
                              type="text"
                              placeholder="Token ID"
                              value={ri.item_id}
                              onChange={e =>
                                handleRequiredItemChange(
                                  index,
                                  riIndex,
                                  'item_id',
                                  e.target.value
                                )
                              }
                              className="admin-input"
                            />
                            <input
                              type="text"
                              placeholder="Item name"
                              value={ri.name}
                              onChange={e =>
                                handleRequiredItemChange(
                                  index,
                                  riIndex,
                                  'name',
                                  e.target.value
                                )
                              }
                              className="admin-input"
                            />
                            <input
                              type="text"
                              placeholder="Marketplace URL"
                              value={ri.url}
                              onChange={e =>
                                handleRequiredItemChange(
                                  index,
                                  riIndex,
                                  'url',
                                  e.target.value
                                )
                              }
                              className="admin-input"
                            />
                            {entry.requiredItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeRequiredItem(index, riIndex)}
                                className="rainbowBtn"
                              >
                                Remove Item
                              </button>
                            )}
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={() => addRequiredItem(index)}
                        className="rainbowBtn"
                      >
                        + Add Required Item
                      </button>

                      {/* Your existing flat fields */}
                      {[
                        'discount_code',
                        'discount_link',
                        'exclusive_product_url',
                        'deal_image'
                      ].map(field => (
                        <div className="admin-form-group" key={field}>
                          <label style={{ textTransform: 'capitalize' }}>
                            {field.replace(/_/g, ' ')}:
                          </label>
                          <input
                            type="text"
                            value={entry[field]}
                            onChange={e =>
                              handleDealChange(index, field, e.target.value)
                            }
                            className="admin-input"
                          />
                        </div>
                      ))}

                      {/* Active */}
                      <div className="admin-form-group">
                        <label>Active:</label>
                        <input
                          type="checkbox"
                          checked={entry.active}
                          onChange={e =>
                            handleDealChange(index, 'active', e.target.checked)
                          }
                          style={{ marginLeft: '0.5rem' }}
                        />
                      </div>

                      {/* Remove this deal */}
                      {dealsForm.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDealEntry(index)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          Remove Deal
                        </button>
                      )}
                    </div>
                  ))}

                  <button type="button" onClick={addDealEntry} className="rainbowBtn">
                    Add Deal
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitDeals}
                    className="rainbowBtn"
                    style={{ marginLeft: '1rem' }}
                  >
                    Save Deals
                  </button>
                </div>
              </div>

              {/* Right column: existing deals list */}
              <div className="admin-column">
                <div className="admin-existing-list">
                  <h4 style={{ color: 'white' }}>Existing Deals</h4>
                  {(!Array.isArray(existingDeals) || existingDeals.length === 0) ? (
                    <p>No deals yet.</p>
                  ) : (
                    existingDeals.map(d => (
                      <div
                        key={d.id}
                        className="admin-existing-item"
                      >
                        <span>
                          {d.deal_name} ({d.collection})
                          <span style={{ 
                            marginLeft: '0.5rem',
                            padding: '0.2rem 0.5rem',
                            borderRadius: '0.3rem',
                            fontSize: '0.7rem',
                            fontWeight: 'bold',
                            backgroundColor: d.deal_type === 'dcl_wearable' ? '#8a2be2' : '#ff8c00',
                            color: 'white'
                          }}>
                            {d.deal_type === 'dcl_wearable' ? 'WEARABLE' : 'PHYGITAL'}
                          </span>
                          {d.rng_enabled && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '0.3rem',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              backgroundColor: '#22c55e',
                              color: 'white'
                            }}>
                              🎲 RNG
                            </span>
                          )}
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {d.rng_enabled && (
                            <button
                              onClick={() => {
                                setRngTestItem(d);
                                setRngTestItemType('deal');
                                setShowRNGTestModal(true);
                              }}
                              style={{
                                padding: '0.3rem 0.6rem',
                                backgroundColor: '#8a2be2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}
                              title="Test RNG rolls and lookup historical rolls"
                            >
                              🎲 RNG
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setDealToDeleteId(d.id);
                              setShowDeleteDealModal(true);
                            }}
                            className="admin-delete-btn"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle for 🎮 DCL Wearable Deals Manager */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowDCLDealsSection(!showDCLDealsSection)}
          >
            {showDCLDealsSection ? '▼' : '►'} 🎮 DCL Wearable Deals
          </h2>
          {showDCLDealsSection && (
            <div className="admin-two-column">
              {/* Left column: new DCL deals form */}
              <div className="admin-column">
                <div className="admin-form">
                  {dclDealsForm.map((entry, index) => (
                    <div key={index} className="admin-upgrade-option">
                      {/* Deal name */}
                      <div className="admin-form-group">
                        <label>Deal name:</label>
                        <input
                          type="text"
                          value={entry.deal_name}
                          onChange={e =>
                            handleDCLDealChange(index, 'deal_name', e.target.value)
                          }
                          className="admin-input"
                        />
                      </div>

                      {/* RNG Toggle */}
                      <div className="admin-form-group" style={{ 
                        border: '1px solid rgba(138, 43, 226, 0.3)', 
                        borderRadius: '8px', 
                        padding: '1rem', 
                        marginTop: '1rem',
                        backgroundColor: 'rgba(138, 43, 226, 0.05)'
                      }}>
                        <h4 style={{ color: '#8a2be2', marginTop: 0, marginBottom: '1rem' }}>🎲 Random Reward Selection</h4>
                        <div className="admin-form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={entry.rng_enabled}
                              onChange={e => handleDCLDealChange(index, 'rng_enabled', e.target.checked)}
                            />
                            Enable Random Reward Selection
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            When enabled, users will receive one random item from the items below instead of all items.
                          </small>
                        </div>

                        {entry.rng_enabled && (
                          <div style={{ marginTop: '1rem' }}>
                            <h5 style={{ color: '#8a2be2', marginBottom: '0.5rem' }}>Item Chances</h5>
                            {entry.dealItems.map((item, itemIndex) => (
                              <div key={itemIndex} style={{ 
                                border: '1px solid rgba(138, 43, 226, 0.3)', 
                                borderRadius: '6px', 
                                padding: '0.75rem', 
                                marginBottom: '0.75rem',
                                backgroundColor: 'rgba(138, 43, 226, 0.1)'
                              }}>
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <strong>Item #{item.dcl_item_id}</strong>
                                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                                    {dclCollections.find(c => c.collection_address === item.dcl_collection_address)?.title || 'Unknown Collection'}
                                  </div>
                                </div>

                                <div className="admin-form-group">
                                  <label>Chance (%):</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={item.chance === undefined ? '' : item.chance}
                                    onChange={e => handleDCLDealItemChange(index, itemIndex, 'chance', e.target.value === '' ? '' : Number(e.target.value))}
                                    className="admin-input"
                                    placeholder="e.g. 25.5"
                                  />
                                </div>
                              </div>
                            ))}
                            
                            <div style={{ 
                              marginTop: '1rem', 
                              padding: '0.75rem',
                              backgroundColor: 'rgba(138, 43, 226, 0.1)',
                              borderRadius: '6px',
                              border: '1px solid rgba(138, 43, 226, 0.3)'
                            }}>
                              <strong style={{ color: '#8a2be2' }}>Total Chance: </strong>
                              {entry.dealItems.reduce((sum, item) => sum + (parseFloat(item.chance) || 0), 0).toFixed(2)}%
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Deal Items Section */}
                      <div className="admin-form-group" style={{ border: '1px solid #444', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
                        <h4 style={{ color: '#8a2be2', marginTop: 0, marginBottom: '1rem' }}>🎮 DCL Wearable Items</h4>
                        
                        {/* RNG Toggle */}
                        <div className="admin-form-group" style={{ marginBottom: '1rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={entry.rng_enabled}
                              onChange={e => handleDCLDealChange(index, 'rng_enabled', e.target.checked)}
                            />
                            Enable Random Reward Selection
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            When enabled, users will receive one random item from the items below instead of all items.
                          </small>
                        </div>
                        
                        <small style={{ color: '#888', fontSize: '0.8rem', marginBottom: '1rem', display: 'block' }}>
                          Configure the DCL wearable items that will be sold together in this deal.
                        </small>
                        
                        {entry.dealItems.map((item, itemIndex) => (
                          <div key={itemIndex} className="admin-recipe-item" style={{ 
                            border: '1px solid rgba(138, 43, 226, 0.3)', 
                            borderRadius: '6px', 
                            padding: '0.75rem', 
                            marginBottom: '0.75rem',
                            backgroundColor: 'rgba(138, 43, 226, 0.05)'
                          }}>
                            <div className="admin-form-group">
                              <label>DCL Collection Item:</label>
                              <select
                                value={item.dcl_collection_item_id || ''}
                                onChange={e => {
                                  const selectedItemId = e.target.value;
                                  const selectedCollection = dclCollections.find(c => c.id.toString() === selectedItemId);
                                  
                                  if (selectedCollection) {
                                    handleDCLDealItemChange(index, itemIndex, 'dcl_collection_address', selectedCollection.collection_address);
                                    handleDCLDealItemChange(index, itemIndex, 'dcl_collection_item_id', selectedCollection.id);
                                    handleDCLDealItemChange(index, itemIndex, 'deal_image', selectedCollection.image || '');
                                    // Auto-populate item ID with URN item ID if available
                                    if (selectedCollection.urn_item_id !== null) {
                                      handleDCLDealItemChange(index, itemIndex, 'dcl_item_id', selectedCollection.urn_item_id);
                                    }
                                  } else {
                                    handleDCLDealItemChange(index, itemIndex, 'dcl_collection_address', '');
                                    handleDCLDealItemChange(index, itemIndex, 'dcl_collection_item_id', '');
                                    handleDCLDealItemChange(index, itemIndex, 'dcl_item_id', '');
                                    handleDCLDealItemChange(index, itemIndex, 'deal_image', '');
                                  }
                                }}
                                className="admin-select"
                              >
                                <option value="">– pick collection item –</option>
                                {dclCollections.map(c => (
                                  <option key={c.id} value={c.id}>
                                    {c.title} (URN ID: {c.urn_item_id || c.token_id})
                                  </option>
                                ))}
                              </select>
                            </div>
                            
                            <div className="admin-form-group">
                              <label>DCL Item ID:</label>
                              <input
                                type="text"
                                value={item.dcl_item_id}
                                onChange={e => handleDCLDealItemChange(index, itemIndex, 'dcl_item_id', e.target.value)}
                                className="admin-input"
                                placeholder="e.g. 0, 1, 2..."
                              />
                            </div>
                            
                            <div className="admin-form-group">
                              <label>Price (MANA):</label>
                              <input
                                type="number"
                                step="0.000001"
                                min="0"
                                value={item.dcl_price_mana}
                                onChange={e => handleDCLDealItemChange(index, itemIndex, 'dcl_price_mana', e.target.value)}
                                className="admin-input"
                                placeholder="e.g. 100.5"
                              />
                            </div>
                            
                            <div className="admin-form-group">
                              <label>Max Supply (optional):</label>
                              <input
                                type="number"
                                min="1"
                                value={item.dcl_max_supply}
                                onChange={e => handleDCLDealItemChange(index, itemIndex, 'dcl_max_supply', e.target.value)}
                                className="admin-input"
                                placeholder="Leave empty for unlimited"
                              />
                            </div>
                            
                            <div className="admin-form-group">
                              <label>Treasury Address (receives MANA payments):</label>
                              <input
                                type="text"
                                value={item.dcl_beneficiary_address}
                                onChange={e => handleDCLDealItemChange(index, itemIndex, 'dcl_beneficiary_address', e.target.value)}
                                className="admin-input"
                                placeholder="0x55CA751383CA801ABEBa78Bbb8b2DE5E2107769A"
                              />
                              <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                Default: 0x55CA751383CA801ABEBa78Bbb8b2DE5E2107769A (DOCTORdripp Treasury)
                              </small>
                            </div>

                            {/* RNG Chance Field - Only show if RNG is enabled */}
                            {entry.rng_enabled && (
                              <div className="admin-form-group">
                                <label>Chance (%):</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={item.chance === undefined ? '' : item.chance}
                                  onChange={e => handleDCLDealItemChange(index, itemIndex, 'chance', e.target.value === '' ? '' : Number(e.target.value))}
                                  className="admin-input"
                                  placeholder="e.g. 25.5"
                                />
                                <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                  Probability of this item being selected when RNG is enabled
                                </small>
                              </div>
                            )}
                            
                            <div className="admin-form-group">
                              <label>Deal Image URL:</label>
                              <input
                                type="text"
                                value={item.deal_image}
                                onChange={e => handleDCLDealItemChange(index, itemIndex, 'deal_image', e.target.value)}
                                className="admin-input"
                                placeholder="Auto-populated from collection metadata"
                              />
                              <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                🤖 Automatically pulled from collection metadata when you select a DCL collection above. You can override if needed.
                              </small>
                              {item.deal_image && (
                                <div style={{ marginTop: '0.5rem' }}>
                                  <img 
                                    src={item.deal_image} 
                                    alt="Deal preview" 
                                    style={{ 
                                      maxWidth: '100px', 
                                      maxHeight: '100px', 
                                      borderRadius: '8px',
                                      border: '1px solid #444'
                                    }} 
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                </div>
                              )}
                            </div>
                            
                            {/* Auto-generated DCL Marketplace Link */}
                            {item.dcl_collection_address && item.dcl_item_id && (
                              <div style={{ 
                                marginTop: '0.5rem', 
                                padding: '0.5rem', 
                                backgroundColor: 'rgba(138, 43, 226, 0.1)', 
                                border: '1px solid rgba(138, 43, 226, 0.3)',
                                borderRadius: '6px'
                              }}>
                                <div style={{ fontSize: '0.8rem', color: '#8a2be2', marginBottom: '0.25rem', fontWeight: '600' }}>
                                  🔗 DCL Marketplace Preview:
                                </div>
                                <a 
                                  href={`https://decentraland.org/marketplace/contracts/${item.dcl_collection_address}/items/${item.dcl_item_id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{ 
                                    fontSize: '0.7rem', 
                                    color: '#8a2be2', 
                                    textDecoration: 'underline',
                                    wordBreak: 'break-all'
                                  }}
                                >
                                  View Item #{item.dcl_item_id} in Marketplace
                                </a>
                              </div>
                            )}
                            
                            {entry.dealItems.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeDCLDealItem(index, itemIndex)}
                                className="rainbowBtn"
                                style={{ 
                                  marginTop: '0.5rem',
                                  fontSize: '0.8rem',
                                  padding: '0.4rem 0.8rem',
                                  backgroundColor: '#e74c3c'
                                }}
                              >
                                Remove Item
                              </button>
                            )}
                          </div>
                        ))}
                        
                        <button
                          type="button"
                          onClick={() => addDCLDealItem(index)}
                          className="rainbowBtn"
                          style={{ 
                            marginTop: '0.5rem',
                            fontSize: '0.8rem',
                            padding: '0.4rem 0.8rem'
                          }}
                        >
                          Add Item
                        </button>

                        {/* Show total chance if RNG is enabled */}
                        {entry.rng_enabled && entry.dealItems.length > 0 && (
                          <div style={{ 
                            marginTop: '1rem', 
                            padding: '0.75rem',
                            backgroundColor: 'rgba(138, 43, 226, 0.1)',
                            borderRadius: '6px',
                            border: '1px solid rgba(138, 43, 226, 0.3)'
                          }}>
                            <strong style={{ color: '#8a2be2' }}>Total Chance: </strong>
                            {entry.dealItems.reduce((sum, item) => sum + (parseFloat(item.chance) || 0), 0).toFixed(2)}%
                          </div>
                        )}
                      </div>

                      {/* Deal Limits Section */}
                      <div className="admin-form-group" style={{ border: '1px solid #444', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
                        <h4 style={{ color: '#fff', marginTop: 0, marginBottom: '1rem' }}>🚫 Deal Limits & Multi-Mint</h4>
                        
                        {/* Limit Per Wallet */}
                        <div className="admin-form-group">
                          <label>Limit Per Wallet:</label>
                          <input
                            type="number"
                            value={entry.limit_per_wallet}
                            onChange={e =>
                              handleDCLDealChange(index, 'limit_per_wallet', e.target.value)
                            }
                            className="admin-input"
                            placeholder="Leave empty for unlimited"
                            min="1"
                          />
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            Maximum wearables a single wallet can purchase (empty = unlimited)
                          </small>
                        </div>

                        {/* Per-Token Limits */}
                        <div className="admin-form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={entry.limit_per_token_enabled}
                              onChange={e =>
                                handleDCLDealChange(index, 'limit_per_token_enabled', e.target.checked)
                              }
                            />
                            Enable Per-Token Limits
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            Prevent the same NFT from being used multiple times (anti-transfer exploit)
                          </small>
                        </div>

                        {entry.limit_per_token_enabled && (
                          <div className="admin-form-group">
                            <label>Uses Per Token:</label>
                            <input
                              type="number"
                              value={entry.limit_per_token}
                              onChange={e =>
                                handleDCLDealChange(index, 'limit_per_token', parseInt(e.target.value) || 1)
                              }
                              className="admin-input"
                              placeholder="1"
                              min="1"
                              max="100"
                            />
                            <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                              How many times each individual NFT can be used for this deal
                            </small>
                          </div>
                        )}

                        {/* Daily Limit */}
                        <div className="admin-form-group">
                          <label>Daily Limit (Global):</label>
                          <input
                            type="number"
                            value={entry.daily_limit}
                            onChange={e =>
                              handleDCLDealChange(index, 'daily_limit', e.target.value)
                            }
                            className="admin-input"
                            placeholder="Leave empty for unlimited"
                            min="1"
                          />
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            Maximum wearables that can be purchased per day across all wallets (empty = unlimited)
                          </small>
                        </div>

                        {/* Multi-Mint Enabled */}
                        <div className="admin-form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="checkbox"
                              checked={entry.multi_mint_enabled}
                              onChange={e =>
                                handleDCLDealChange(index, 'multi_mint_enabled', e.target.checked)
                              }
                            />
                            Enable Multi-Mint
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            Allow users to purchase multiple wearables in a single transaction (up to limits)
                          </small>
                        </div>

                        {/* Expiration DateTime */}
                        <div className="admin-form-group">
                          <label>
                            <span style={{ fontWeight: 'bold' }}>⏰ Expiration Date/Time (Optional)</span>
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                              <input
                                type="datetime-local"
                                value={entry.expires_at || ''}
                                onChange={e => handleDCLDealChange(index, 'expires_at', e.target.value)}
                                className="admin-input"
                                style={{ flex: 1, padding: '0.5rem' }}
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  const date = new Date();
                                  date.setDate(date.getDate() + 1);
                                  handleDCLDealChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                +1 Day
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const date = new Date();
                                  date.setDate(date.getDate() + 3);
                                  handleDCLDealChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                +3 Days
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  const date = new Date();
                                  date.setDate(date.getDate() + 7);
                                  handleDCLDealChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                }}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: '#6f42c1',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '0.85rem',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                +7 Days
                              </button>
                            </div>
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            Deal will automatically end at this date/time. Leave empty for no expiration.
                          </small>
                        </div>

                        {/* Strict Supply Pool Mode */}
                        <div className="admin-form-group">
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={entry.strict_supply_pool || false}
                              onChange={e => handleDCLDealChange(index, 'strict_supply_pool', e.target.checked)}
                            />
                            <span style={{ fontWeight: 'bold' }}>🔒 Strict Supply Pool Mode</span>
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            If enabled, deal ends when ANY linked supply pool item runs out. If disabled, only sold-out items are removed from RNG (dynamic pool).
                          </small>
                        </div>
                      </div>

                      {/* AND / OR logic */}
                      <div className="admin-form-group">
                        <label>Require all items?</label>
                        <select
                          value={entry.logic}
                          onChange={e => handleDCLLogicChange(index, e.target.value)}
                          className="admin-select"
                        >
                          <option value="AND">Yes (AND)</option>
                          <option value="OR">No (OR)</option>
                        </select>
                      </div>

                      {/* Required Items section with improved UX */}
                      <div className="admin-form-group">
                        <label>Required NFT Collections/Items:</label>
                        
                        {/* Toggle for custom vs approved collections */}
                        <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '8px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={entry.useCustomCollection}
                              onChange={e => {
                                const newDeals = [...dclDealsForm];
                                newDeals[index].useCustomCollection = e.target.checked;
                                // Reset required items when switching modes
                                newDeals[index].requiredItems = [{ collection: '', item_id: '', name: '', url: '' }];
                                setDclDealsForm(newDeals);
                              }}
                              style={{ marginRight: '0.5rem' }}
                            />
                            Use custom collection address (advanced)
                          </label>
                          <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                            {entry.useCustomCollection 
                              ? 'Enter any Polygon NFT collection address manually'
                              : 'Select from your approved collections for faster setup'
                            }
                          </small>
                        </div>

                        {/* Approved Collections Mode (when custom is OFF) */}
                        {!entry.useCustomCollection && (
                          <>
                            {entry.requiredItems.map((requirement, reqIndex) => (
                              <div key={reqIndex} style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.9rem', fontWeight: '600' }}>
                                  Select Approved Collection {reqIndex + 1}:
                                </label>
                                <select
                                  value={
                                    requirement?.collection_item_id || 
                                    (requirement?.collection ? collections.find(c => c.collection_address === requirement.collection)?.id || "" : "")
                                  }
                                  onChange={e => {
                                    handleDCLRequiredItemChange(index, reqIndex, 'collection_item_selection', e.target.value);
                                  }}
                                  className="admin-select"
                                  style={{ marginBottom: '0.5rem' }}
                                >
                                  <option value="">-- No NFT requirement --</option>
                                  {collections.map(col => (
                                    <option key={col.id} value={col.id}>
                                      {col.title} {col.urn_item_id !== null ? `(URN ID: ${col.urn_item_id})` : `(${col.collection_address.slice(0, 8)}...)`}
                                    </option>
                                  ))}
                                </select>
                                
                                {/* Show selected collection info and optional item ID */}
                                {requirement?.collection && (
                                  <div style={{ 
                                    padding: '0.5rem', 
                                    backgroundColor: 'rgba(34, 197, 94, 0.1)', 
                                    border: '1px solid rgba(34, 197, 94, 0.3)',
                                    borderRadius: '6px',
                                    marginTop: '0.5rem'
                                  }}>
                                    <div style={{ fontSize: '0.85rem', color: '#22c55e', marginBottom: '0.5rem' }}>
                                      ✅ <strong>{requirement?.name}</strong>
                                    </div>
                                    <div style={{ fontSize: '0.75rem', color: '#888', fontFamily: 'monospace', marginBottom: '0.5rem' }}>
                                      {requirement?.collection}
                                    </div>
                                    
                                    {/* Optional Item ID and Quantity fields */}
                                    <div style={{ marginTop: '0.5rem' }}>
                                      <div style={{ marginBottom: '0.5rem' }}>
                                        <label style={{ fontSize: '0.8rem', color: '#ccc', marginBottom: '0.25rem', display: 'block' }}>
                                          Specific Token ID (optional):
                                        </label>
                                        <input
                                          type="text"
                                          placeholder="Leave blank for any NFT from this collection"
                                          value={requirement?.item_id || ''}
                                          onChange={e => {
                                            const newDeals = [...dclDealsForm];
                                            newDeals[index].requiredItems[reqIndex].item_id = e.target.value;
                                            
                                            // Auto-generate marketplace URL if we have both collection and item_id
                                            if (newDeals[index].requiredItems[reqIndex].collection && e.target.value) {
                                              newDeals[index].requiredItems[reqIndex].url = 
                                                `https://decentraland.org/marketplace/contracts/${newDeals[index].requiredItems[reqIndex].collection}/items/${e.target.value}`;
                                            } else {
                                              newDeals[index].requiredItems[reqIndex].url = '';
                                            }
                                            
                                            setDclDealsForm(newDeals);
                                          }}
                                          className="admin-input"
                                          style={{ 
                                            fontSize: '0.8rem',
                                            padding: '0.4rem',
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(255,255,255,0.2)'
                                          }}
                                        />
                                      </div>
                                      <div style={{ width: '100px' }}>
                                        <label style={{ fontSize: '0.8rem', color: '#ccc', marginBottom: '0.25rem', display: 'block' }}>
                                          Quantity:
                                        </label>
                                        <input
                                          type="number"
                                          min="1"
                                          placeholder="1"
                                          value={requirement?.quantity || 1}
                                          onChange={e => {
                                            const newDeals = [...dclDealsForm];
                                            newDeals[index].requiredItems[reqIndex].quantity = parseInt(e.target.value) || 1;
                                            setDclDealsForm(newDeals);
                                          }}
                                          className="admin-input"
                                          style={{ 
                                            fontSize: '0.8rem',
                                            padding: '0.4rem',
                                            backgroundColor: 'rgba(255,255,255,0.05)',
                                            border: '1px solid rgba(255,255,255,0.2)'
                                          }}
                                        />
                                      </div>
                                    </div>
                                    
                                    {/* Show auto-generated marketplace link when available */}
                                    {requirement?.url && (
                                      <div style={{ 
                                        marginTop: '0.5rem',
                                        padding: '0.5rem',
                                        backgroundColor: 'rgba(138, 43, 226, 0.1)',
                                        border: '1px solid rgba(138, 43, 226, 0.3)',
                                        borderRadius: '6px'
                                      }}>
                                        <div style={{ fontSize: '0.8rem', color: '#8a2be2', marginBottom: '0.25rem', fontWeight: '600' }}>
                                          🔗 Auto-Generated Marketplace Link:
                                        </div>
                                        <a 
                                          href={requirement.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ 
                                            fontSize: '0.7rem', 
                                            color: '#8a2be2', 
                                            textDecoration: 'underline',
                                            wordBreak: 'break-all'
                                          }}
                                        >
                                          {requirement.url}
                                        </a>
                                      </div>
                                    )}
                                    
                                    {/* Remove Requirement button */}
                                    {entry.requiredItems.length > 1 && (
                                      <button
                                        type="button"
                                        onClick={() => removeDCLRequiredItem(index, reqIndex)}
                                        className="rainbowBtn"
                                        style={{
                                          fontSize: '0.8rem',
                                          padding: '0.4rem 0.8rem',
                                          backgroundColor: '#e74c3c',
                                          marginTop: '0.5rem'
                                        }}
                                      >
                                        Remove Requirement
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                          </>
                        )}

                        {/* Add Requirement button - show for both modes */}
                        {(!entry.useCustomCollection && entry.requiredItems[0]?.collection) || entry.useCustomCollection ? (
                          <button
                            type="button"
                            onClick={() => addDCLRequiredItem(index)}
                            className="rainbowBtn"
                            style={{ marginTop: '0.5rem' }}
                          >
                            + Add Requirement
                          </button>
                        ) : null}

                        {/* Custom Collection Fields (when custom is ON) */}
                        {entry.useCustomCollection && (
                          <>
                            {entry.requiredItems.map((item, itemIndex) => (
                              <div key={itemIndex} className="admin-recipe-item" style={{ 
                                border: '1px solid rgba(34, 197, 94, 0.3)', 
                                borderRadius: '6px', 
                                padding: '0.75rem', 
                                marginBottom: '0.75rem',
                                backgroundColor: 'rgba(34, 197, 94, 0.05)'
                              }}>
                                <div className="admin-form-group">
                                  <label>Collection Address:</label>
                                  <input
                                    type="text"
                                    placeholder="0x1234567890abcdef..."
                                    value={item.collection}
                                    onChange={e =>
                                      handleDCLRequiredItemChange(index, itemIndex, 'collection', e.target.value)
                                    }
                                    className="admin-input"
                                  />
                                </div>
                                
                                <div className="admin-form-group">
                                  <label>Item ID (optional):</label>
                                  <input
                                    type="text"
                                    placeholder="Leave blank for any NFT from this collection"
                                    value={item.item_id}
                                    onChange={e =>
                                      handleDCLRequiredItemChange(index, itemIndex, 'item_id', e.target.value)
                                    }
                                    className="admin-input"
                                  />
                                </div>
                                
                                <div className="admin-form-group">
                                  <label>Quantity Required:</label>
                                  <input
                                    type="number"
                                    min="1"
                                    placeholder="1"
                                    value={item.quantity}
                                    onChange={e =>
                                      handleDCLRequiredItemChange(index, itemIndex, 'quantity', parseInt(e.target.value) || 1)
                                    }
                                    className="admin-input"
                                  />
                                </div>
                                
                                <div className="admin-form-group">
                                  <label>Display Name:</label>
                                  <input
                                    type="text"
                                    placeholder="Collection display name"
                                    value={item.name}
                                    onChange={e =>
                                      handleDCLRequiredItemChange(index, itemIndex, 'name', e.target.value)
                                    }
                                    className="admin-input"
                                  />
                                </div>
                                
                                <div className="admin-form-group">
                                  <label>Marketplace URL (optional):</label>
                                  <input
                                    type="text"
                                    placeholder="https://opensea.io/collection/..."
                                    value={item.url}
                                    onChange={e =>
                                      handleDCLRequiredItemChange(index, itemIndex, 'url', e.target.value)
                                    }
                                    className="admin-input"
                                  />
                                </div>
                                
                                {entry.requiredItems.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeDCLRequiredItem(index, itemIndex)}
                                    className="rainbowBtn"
                                    style={{
                                      fontSize: '0.8rem',
                                      padding: '0.4rem 0.8rem',
                                      backgroundColor: '#e74c3c',
                                      marginTop: '0.5rem'
                                    }}
                                  >
                                    Remove Requirement
                                  </button>
                                )}
                              </div>
                            ))}

                            <button
                              type="button"
                              onClick={() => addDCLRequiredItem(index)}
                              className="rainbowBtn"
                              style={{ marginTop: '0.5rem' }}
                            >
                              + Add Requirement
                            </button>
                          </>
                        )}
                      </div>

                      

                      {/* Active */}
                      <div className="admin-form-group">
                        <label>Active:</label>
                        <input
                          type="checkbox"
                          checked={entry.active}
                          onChange={e =>
                            handleDCLDealChange(index, 'active', e.target.checked)
                          }
                          style={{ marginLeft: '0.5rem' }}
                        />
                      </div>

                      {/* Remove this deal */}
                      {dclDealsForm.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDCLDealEntry(index)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem'
                          }}
                        >
                          Remove Deal
                        </button>
                      )}
                    </div>
                  ))}

                  <button type="button" onClick={addDCLDealEntry} className="rainbowBtn">
                    Add DCL Deal
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitDCLDeals}
                    className="rainbowBtn"
                    style={{ marginLeft: '1rem' }}
                  >
                    Save DCL Deals
                  </button>
                </div>
              </div>

              {/* Right column: existing DCL deals list */}
              <div className="admin-column">
                <div className="admin-existing-list">
                  <h4 style={{ color: 'white' }}>Existing DCL Wearable Deals</h4>
                  {(!Array.isArray(existingDCLDeals) || existingDCLDeals.length === 0) ? (
                    <p>No DCL wearable deals yet.</p>
                  ) : (
                    existingDCLDeals.map(d => (
                      <div
                        key={d.id}
                        className="admin-existing-item"
                      >
                        <span>
                          {d.deal_name}
                          {d.rng_enabled && (
                            <span style={{ 
                              marginLeft: '0.5rem',
                              padding: '0.2rem 0.5rem',
                              borderRadius: '0.3rem',
                              fontSize: '0.7rem',
                              fontWeight: 'bold',
                              backgroundColor: '#22c55e',
                              color: 'white'
                            }}>
                              🎲 RNG
                            </span>
                          )}
                          <br />
                          <small style={{ opacity: 0.7 }}>
                            {d.dcl_price_mana} MANA | Item #{d.dcl_item_id}
                          </small>
                        </span>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          {d.rng_enabled && (
                            <button
                              onClick={() => {
                                setRngTestItem(d);
                                setRngTestItemType('deal');
                                setShowRNGTestModal(true);
                              }}
                              style={{
                                padding: '0.3rem 0.6rem',
                                backgroundColor: '#8a2be2',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '0.5rem',
                                cursor: 'pointer',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}
                              title="Test RNG rolls and lookup historical rolls"
                            >
                              🎲 RNG
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setDclDealToDeleteId(d.id);
                              setShowDeleteDCLDealModal(true);
                            }}
                            className="admin-delete-btn"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle for 🧪 Recipe Manager */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowRecipesSection(!showRecipesSection)}
          >
            {showRecipesSection ? '▼' : '►'} 🧪 Recipe Manager
          </h2>
          {showRecipesSection && (
            <div className="admin-two-column">
              {/* Left Column – New Recipe Form */}
              <div className="admin-column">
                <div className="admin-form">
                  {recipesForm.map((recipe, index) => (
                    <div key={index} className="admin-recipe-container">
                      <h4 style={{ color: 'white', textAlign: 'center', margin: '0 0 1rem 0' }}>
                        Recipe {index + 1}
                      </h4>
                      
                      {/* Recipe Title */}
                      <div className="admin-form-group">
                        <label>Recipe Title:</label>
                        <input
                          type="text"
                          value={recipe.title}
                          onChange={(e) => handleRecipeChange(index, 'title', e.target.value)}
                          placeholder="Recipe name"
                          className="admin-input"
                        />
                      </div>

                      {/* Recipe Type */}
                      <div className="admin-form-group">
                        <label>Recipe Type:</label>
                        <select
                          value={recipe.recipe_type}
                          onChange={(e) => handleRecipeChange(index, 'recipe_type', e.target.value)}
                          className="admin-select"
                        >
                          <option value="recipe">Normal Recipe (Burn Items)</option>
                          <option value="token_gated">Token-Gated Recipe (Burn + Hold)</option>
                          <option value="free_mint">Free Mint (Hold Only)</option>
                        </select>
                      </div>

                      {/* Upgrade Destination - Only show for recipes that require burning */}
                      {recipe.recipe_type !== 'free_mint' && (
                        <div className="admin-form-group">
                          <label>Burn Destination:</label>
                          <input
                            type="text"
                            value={recipe.upgrade_destination}
                            onChange={(e) => handleRecipeChange(index, 'upgrade_destination', e.target.value)}
                            placeholder="Address or BURN"
                            className="admin-input"
                          />
                        </div>
                      )}

                      {/* Token Gate Requirements - Only show for token-gated and free mint */}
                      {(recipe.recipe_type === 'token_gated' || recipe.recipe_type === 'free_mint') && (
                        <>
                          <h4 style={{ color: 'white', margin: '1rem 0 0.5rem 0' }}>Token Gate Requirements</h4>
                          <div className="admin-form-group">
                            <label>Access Requirements Logic:</label>
                            <select
                              value={recipe.token_gate_logic}
                              onChange={(e) => handleRecipeChange(index, 'token_gate_logic', e.target.value)}
                              className="admin-select"
                            >
                              <option value="AND">Must own ALL collections (AND)</option>
                              <option value="OR">Must own ANY ONE collection (OR)</option>
                            </select>
                          </div>
                          {recipe.token_gate_requirements.map((gate, gateIndex) => (
                            <div key={gateIndex} className="admin-recipe-item">
                              <div className="admin-form-group">
                                <label>Collection:</label>
                                <select
                                  value={(() => {
                                    if (!gate.collection) return "";
                                    // Find the collection item that matches both collection address and URN item ID
                                    const urnReq = recipe.token_gate_urn_requirements?.[gateIndex];
                                    
                                    if (urnReq && urnReq.urn_item_id) {
                                      const matchingItem = collections.find(c => 
                                        c.collection_address.toLowerCase() === gate.collection.toLowerCase() && 
                                        c.urn_item_id === urnReq.urn_item_id
                                      );
                                      return matchingItem ? matchingItem.id : "";
                                    }
                                    // Fallback to first item with matching collection address
                                    const fallbackItem = collections.find(c => c.collection_address.toLowerCase() === gate.collection.toLowerCase());
                                    return fallbackItem ? fallbackItem.id : "";
                                  })()}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    handleTokenGateChange(index, gateIndex, 'collection_item_selection', value);
                                  }}
                                  className="admin-select"
                                >
                                  <option value="">– pick collection item –</option>
                                  {collections.map(c => (
                                    <option key={c.id} value={c.id}>
                                      {c.title} {c.urn_item_id !== null ? `(URN ID: ${c.urn_item_id})` : `(Blockchain Token #${c.token_id})`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div className="admin-form-group">
                                <label>Item ID (optional):</label>
                                <input
                                  type="text"
                                  value={gate.item_id}
                                  onChange={(e) => handleTokenGateChange(index, gateIndex, 'item_id', e.target.value)}
                                  placeholder="Specific token ID (leave empty for any)"
                                  className="admin-input"
                                />
                              </div>
                              <div className="admin-form-group">
                                <label>Quantity:</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={gate.quantity}
                                  onChange={(e) => handleTokenGateChange(index, gateIndex, 'quantity', parseInt(e.target.value) || 1)}
                                  className="admin-input"
                                />
                              </div>
                              <div className="admin-form-group">
                                <label>Marketplace Link (optional):</label>
                                <input
                                  type="url"
                                  value={gate.marketplace_link || ''}
                                  onChange={(e) => handleTokenGateChange(index, gateIndex, 'marketplace_link', e.target.value)}
                                  placeholder="https://opensea.io/collection/..."
                                  className="admin-input"
                                />
                              </div>
                              {recipe.token_gate_requirements.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeTokenGate(index, gateIndex)}
                                  className="rainbowBtn"
                                  style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                                >
                                  Remove Gate
                                </button>
                              )}
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => addTokenGate(index)}
                            className="rainbowBtn"
                            style={{ marginBottom: '1rem' }}
                          >
                            + Add Token Gate
                          </button>

                          {/* Per-Token Limits Section */}
                          <div className="admin-form-group" style={{ border: '1px solid #444', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
                            <h4 style={{ color: '#fff', marginTop: 0, marginBottom: '1rem' }}>🔒 Per-Token Limits</h4>
                            
                            <div className="admin-form-group">
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input
                                  type="checkbox"
                                  checked={recipe.limit_per_token_enabled}
                                  onChange={e =>
                                    handleRecipeChange(index, 'limit_per_token_enabled', e.target.checked)
                                  }
                                />
                                Enable Per-Token Limits
                              </label>
                              <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                Prevent the same NFT from being used multiple times (anti-transfer exploit)
                              </small>
                            </div>

                            {recipe.limit_per_token_enabled && (
                              <div className="admin-form-group">
                                <label>Uses Per Token:</label>
                                <input
                                  type="number"
                                  value={recipe.limit_per_token}
                                  onChange={e =>
                                    handleRecipeChange(index, 'limit_per_token', parseInt(e.target.value) || 1)
                                  }
                                  className="admin-input"
                                  placeholder="1"
                                  min="1"
                                  max="100"
                                />
                                <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                                  How many times each individual NFT can be used for this recipe
                                </small>
                              </div>
                            )}
                          </div>

                          {/* Expiration DateTime */}
                          <div className="admin-form-group" style={{ marginTop: '1rem' }}>
                            <label>
                              <span style={{ fontWeight: 'bold' }}>⏰ Expiration Date/Time (Optional)</span>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                                <input
                                  type="datetime-local"
                                  value={recipe.expires_at || ''}
                                  onChange={e => handleRecipeChange(index, 'expires_at', e.target.value)}
                                  className="admin-input"
                                  style={{ flex: 1, padding: '0.5rem' }}
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setDate(date.getDate() + 1);
                                    handleRecipeChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                  }}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  +1 Day
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setDate(date.getDate() + 3);
                                    handleRecipeChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                  }}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    background: '#17a2b8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  +3 Days
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const date = new Date();
                                    date.setDate(date.getDate() + 7);
                                    handleRecipeChange(index, 'expires_at', date.toISOString().slice(0, 16));
                                  }}
                                  style={{
                                    padding: '0.5rem 0.75rem',
                                    background: '#6f42c1',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.85rem',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  +7 Days
                                </button>
                              </div>
                            </label>
                            <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                              Recipe will automatically end at this date/time. Leave empty for no expiration.
                            </small>
                          </div>

                          {/* Strict Supply Pool Mode */}
                          <div className="admin-form-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={recipe.strict_supply_pool || false}
                                onChange={e => handleRecipeChange(index, 'strict_supply_pool', e.target.checked)}
                              />
                              <span style={{ fontWeight: 'bold' }}>🔒 Strict Supply Pool Mode</span>
                            </label>
                            <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                              If enabled, recipe ends when ANY linked supply pool item runs out. If disabled, only sold-out items are removed from RNG (dynamic pool).
                            </small>
                          </div>
                        </>
                      )}

                      {/* Burn Requirements Logic - Only show for recipes that require burning */}
                      {recipe.recipe_type !== 'free_mint' && (
                        <div className="admin-form-group">
                          <label>Burn Requirements Logic:</label>
                          <select
                            value={recipe.logic}
                            onChange={e => handleRecipeLogicChange(index, e.target.value)}
                            className="admin-select"
                          >
                            <option value="AND">Require ALL items (AND)</option>
                            <option value="OR">Require ANY ONE item (OR)</option>
                          </select>
                        </div>
                      )}

                      {/* Requirements Section - Only show for recipes that require burning */}
                      {recipe.recipe_type !== 'free_mint' && (
                        <>
                          <h4 style={{ color: 'white', margin: '1rem 0 0.5rem 0' }}>Burn Requirements</h4>
                      {recipe.requires.map((req, reqIndex) => (
                        <div key={reqIndex} className="admin-recipe-item">
                          <div className="admin-form-group">
                            <label>Collection Item:</label>
                            <select
                              value={req._selected_item_id || ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                handleRequireChange(index, reqIndex, 'collection_item_selection', value);
                              }}
                              className="admin-select"
                            >
                              <option value="">– pick collection item –</option>
                              {collections.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.title} {c.urn_item_id !== null ? `(URN ID: ${c.urn_item_id})` : `(Blockchain Token #${c.token_id})`}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="admin-form-group">
                            <label>Item ID:</label>
                            <input
                              type="text"
                              value={req.item_id || ""}
                              onChange={(e) => handleRequireChange(index, reqIndex, 'item_id', e.target.value)}
                              placeholder="Token ID (optional - for specific token lockdown)"
                              className="admin-input"
                            />
                          </div>
                          <div className="admin-form-group">
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              value={req.quantity}
                              onChange={(e) => handleRequireChange(index, reqIndex, 'quantity', parseInt(e.target.value) || 1)}
                              className="admin-input"
                            />
                          </div>
                          {recipe.requires.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeRequire(index, reqIndex)}
                              className="rainbowBtn"
                              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                        <button
                          type="button"
                          onClick={() => addRequire(index)}
                          className="rainbowBtn"
                          style={{ marginBottom: '1rem' }}
                        >
                          + Add Requirement
                        </button>

                      </>
                    )}

                      {/* Rewards Section */}
                      <h4 style={{ color: 'white', margin: '1rem 0 0.5rem 0' }}>Rewards</h4>
                      
                      {/* Add RNG Toggle */}
                      <div className="admin-form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={recipe.rng_enabled || false}
                            onChange={(e) => {
                              handleRecipeChange(index, 'rng_enabled', e.target.checked);
                              if (e.target.checked) {
                                // Initialize chances in rewards array
                                const updatedRewards = recipe.rewards.map(r => ({
                                  ...r,
                                  chance: 100 / recipe.rewards.length // Distribute evenly initially
                                }));
                                handleRecipeChange(index, 'rewards', updatedRewards);
                              } else {
                                // Remove chances from rewards
                                const updatedRewards = recipe.rewards.map(({ chance, ...r }) => r);
                                handleRecipeChange(index, 'rewards', updatedRewards);
                              }
                            }}
                            style={{ marginRight: '0.5rem' }}
                          />
                          Enable Random Rewards (RNG)
                        </label>
                        {/* Add Distribute Evenly button when RNG is enabled and there are multiple rewards */}
                        {recipe.rng_enabled && recipe.rewards.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              const evenChance = 100 / recipe.rewards.length;
                              const updatedRewards = recipe.rewards.map(r => ({
                                ...r,
                                chance: evenChance
                              }));
                              handleRecipeChange(index, 'rewards', updatedRewards);
                            }}
                            className="rainbowBtn"
                            style={{
                              fontSize: '0.8rem',
                              padding: '0.4rem 0.8rem',
                              marginLeft: '1rem',
                              background: 'linear-gradient(45deg, #8a2be2, #00bfff)',
                              border: 'none',
                              color: 'white',
                              borderRadius: '4px',
                              cursor: 'pointer'
                            }}
                          >
                            🎲 Distribute Evenly
                          </button>
                        )}
                      </div>

                      {recipe.rewards.map((reward, rewardIndex) => (
                        <div key={rewardIndex} className="admin-recipe-item">
                          <div className="admin-form-group">
                            <label>Collection Item:</label>
                            <select
                              value={
                                reward.collection_item_id || 
                                (reward.collection ? collections.find(c => c.collection_address === reward.collection)?.id || "" : "")
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                
                                if (value) {
                                  // Find the selected collection item by ID (convert string to number)
                                  const selectedItem = collections.find(c => c.id === parseInt(value));
                                  
                                  if (selectedItem) {
                                    handleRewardChange(index, rewardIndex, 'collection', selectedItem.collection_address);
                                    handleRewardChange(index, rewardIndex, 'collection_item_id', selectedItem.id);
                                  }
                                } else {
                                  handleRewardChange(index, rewardIndex, 'collection', '');
                                  handleRewardChange(index, rewardIndex, 'collection_item_id', '');
                                }
                              }}
                              className="admin-select"
                            >
                              <option value="">– pick collection item –</option>
                              {collections.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.title} (Blockchain Token #{c.token_id})
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="admin-form-group">
                            <label>Item ID (URN item ID for reward):</label>
                            <input
                              type="text"
                              value={reward.item_id}
                              onChange={(e) => handleRewardChange(index, rewardIndex, 'item_id', e.target.value)}
                              placeholder="URN item ID (0, 1, 2, etc.)"
                              className="admin-input"
                            />
                            <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                              💡 Enter URN item ID for reward issuance (e.g., 0 for Red Laser Eyes, 1 for Blue Laser Eyes)
                            </small>
                          </div>
                          <div className="admin-form-group">
                            <label>Quantity:</label>
                            <input
                              type="number"
                              min="1"
                              value={reward.quantity}
                              onChange={(e) => handleRewardChange(index, rewardIndex, 'quantity', parseInt(e.target.value) || 1)}
                              className="admin-input"
                            />
                          </div>
                          {/* Add Chance Input for RNG enabled recipes */}
                          {recipe.rng_enabled && (
                            <div className="admin-form-group">
                              <label>Chance (%):</label>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.1"
                                value={reward.chance || 0}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  // Ensure value is between 0 and 100
                                  const clampedValue = Math.min(Math.max(value, 0), 100);
                                  handleRewardChange(index, rewardIndex, 'chance', clampedValue);
                                }}
                                placeholder="0"
                                className="admin-input"
                                style={{ width: '100px' }}
                              />
                              <span style={{ marginLeft: '8px', color: '#666' }}>
                                {reward.chance ? `${reward.chance}%` : '0%'}
                              </span>
                            </div>
                          )}
                          {recipe.rewards.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeReward(index, rewardIndex)}
                              className="rainbowBtn"
                              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addReward(index)}
                        className="rainbowBtn"
                        style={{ marginBottom: '1rem' }}
                      >
                        + Add Reward
                      </button>

                      {/* Claim Limits Section */}
                      <div className="admin-form-group">
                        <label>
                          <input
                            type="checkbox"
                            checked={recipe.limit_claims || false}
                            onChange={(e) => handleRecipeChange(index, 'limit_claims', e.target.checked)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          Limit Claims
                        </label>
                      </div>

                      {recipe.limit_claims && (
                        <>
                          <div className="admin-form-group">
                            <label>Total per Wallet (optional):</label>
                            <input
                              type="number"
                              min="1"
                              value={recipe.limit_per_wallet || ''}
                              onChange={(e) => handleRecipeChange(index, 'limit_per_wallet', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="Unlimited"
                              className="admin-input"
                            />
                          </div>
                          <div className="admin-form-group">
                            <label>Total per 24 Hours (global, optional):</label>
                            <input
                              type="number"
                              min="1"
                              value={recipe.limit_per_24h || ''}
                              onChange={(e) => handleRecipeChange(index, 'limit_per_24h', e.target.value ? parseInt(e.target.value) : null)}
                              placeholder="Unlimited"
                              className="admin-input"
                            />
                          </div>
                        </>
                      )}

                      {/* Active Status */}
                      <div className="admin-form-group">
                        <label>Active:</label>
                        <input
                          type="checkbox"
                          checked={recipe.active}
                          onChange={(e) => handleRecipeChange(index, 'active', e.target.checked)}
                          style={{ marginLeft: '0.5rem' }}
                        />
                      </div>

                      {/* Remove Recipe */}
                      {recipesForm.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRecipeEntry(index)}
                          style={{
                            padding: '0.5rem 1rem',
                            borderRadius: '0.75rem',
                            border: 'none',
                            backgroundColor: '#e74c3c',
                            color: '#fff',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            marginTop: '1rem'
                          }}
                        >
                          Remove Recipe
                        </button>
                      )}
                    </div>
                  ))}

                  <button type="button" onClick={addRecipeEntry} className="rainbowBtn">
                    Add Recipe
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitRecipes}
                    className="rainbowBtn"
                    style={{ marginLeft: '1rem' }}
                  >
                    Save Recipes
                  </button>
                </div>
              </div>

              {/* Right Column – Existing Recipes List */}
              <div className="admin-column">
                <div className="admin-existing-list">
                  <h4 style={{ color: 'white' }}>Existing Recipes</h4>
                  {(!Array.isArray(existingRecipes) || existingRecipes.length === 0) ? (
                    <p>No recipes yet.</p>
                  ) : (
                    existingRecipes.map((recipe) => (
                      <div
                        key={recipe.id}
                        className="admin-existing-item"
                        style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '1rem' }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontWeight: 'bold' }}>{recipe.title}</span>
                            {recipe.rng_enabled && (
                              <span style={{ 
                                padding: '0.2rem 0.5rem',
                                borderRadius: '0.3rem',
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                backgroundColor: '#22c55e',
                                color: 'white'
                              }}>
                                🎲 RNG
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                            {recipe.rng_enabled && (
                              <button
                                onClick={() => {
                                  setRngTestItem(recipe);
                                  setRngTestItemType('recipe');
                                  setShowRNGTestModal(true);
                                }}
                                style={{
                                  padding: '0.3rem 0.6rem',
                                  backgroundColor: '#8a2be2',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '0.5rem',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  fontWeight: 'bold'
                                }}
                                title="Test RNG rolls and lookup historical rolls"
                              >
                                🎲 RNG
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteRecipe(recipe.id)}
                              className="admin-delete-btn"
                              title="Delete recipe"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                        <div style={{ fontSize: '0.9rem', opacity: 0.8, marginTop: '0.5rem' }}>
                          <strong>Type:</strong> {recipe.recipe_type === 'free_mint' ? '🟦 Free Mint' : recipe.recipe_type === 'token_gated' ? '🟣 Token-Gated' : '🟧 Normal Recipe'}
                        </div>
                        {recipe.recipe_type !== 'free_mint' && (
                          <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                            <strong>Burn Requirements ({recipe.logic || 'AND'}):</strong> {recipe.requires?.map(r => `${r.quantity}x ${r.collection.slice(0,6)}...`).join(', ')}
                          </div>
                        )}
                        {(recipe.recipe_type === 'token_gated' || recipe.recipe_type === 'free_mint') && recipe.token_gate_requirements?.length > 0 && (
                          <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                            <strong>Token Gates ({recipe.token_gate_logic || 'AND'}):</strong> {recipe.token_gate_requirements.map(g => `${g.quantity}x ${g.collection.slice(0,6)}...`).join(', ')}
                          </div>
                        )}
                        <div style={{ fontSize: '0.9rem', opacity: 0.8 }}>
                          <strong>Rewards:</strong> {recipe.rewards?.map(r => `${r.quantity}x ${r.collection.slice(0,6)}...`).join(', ')}
                        </div>
                        <div style={{ fontSize: '0.8rem', opacity: 0.6, marginTop: '0.25rem' }}>
                          Status: {recipe.active ? '✅ Active' : '❌ Inactive'}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* =========== SUPPLY POOLS Section =========== */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowSupplyPoolsSection(!showSupplyPoolsSection)}
            style={{ cursor: 'pointer' }}
          >
            {showSupplyPoolsSection ? '▼' : '►'} 📦 Supply Pools
          </h2>
          {showSupplyPoolsSection && (
            <div className="glassmorphism-card" style={{ padding: '1rem' }}>
              <SupplyPoolsSection
                supplyPools={supplyPools}
                supplyPoolForm={supplyPoolForm}
                setSupplyPoolForm={setSupplyPoolForm}
                editingSupplyPool={editingSupplyPool}
                setEditingSupplyPool={setEditingSupplyPool}
                onSaveSupplyPool={handleSaveSupplyPool}
                onDeleteSupplyPool={handleDeleteSupplyPool}
                collections={collections}
                jwtToken={jwtToken}
                existingDCLDeals={existingDCLDeals}
                existingRecipes={existingRecipes}
                mintLaunchingConfig={{
                  selected_deal_ids: selectedMintDeals,
                  selected_recipe_ids: selectedMintRecipes
                }}
              />
            </div>
          )}
        </div>
        {/* END Supply Pools Section */}

        {/* Toggle for 🚰 FAUCET Configuration */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowFaucetSection(!showFaucetSection)}
          >
            {showFaucetSection ? '▼' : '►'} 🚰 FAUCET Configuration
          </h2>
          {showFaucetSection && (
            <div className="glassmorphism-card">
              <div className="admin-form">
                <h3 style={{ color: 'white', textAlign: 'center', margin: '1rem 0' }}>
                  POL Token Faucet Settings
                </h3>

                {/* Current Configuration Display */}
                {currentFaucetConfig && (currentFaucetConfig.enabled !== undefined) && (
                  <div style={{
                    marginBottom: '2rem',
                    padding: '1rem',
                    background: 'rgba(138, 43, 226, 0.1)',
                    border: '1px solid rgba(138, 43, 226, 0.3)',
                    borderRadius: '0.5rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                      <h4 style={{ color: '#8a2be2', margin: 0 }}>📋 Current Configuration</h4>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button
                          type="button"
                          onClick={handleToggleFaucetEnabled}
                          className="rainbowBtn"
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '0.4rem 0.8rem', 
                            backgroundColor: currentFaucetConfig.enabled ? '#f59e0b' : '#22c55e',
                            backgroundImage: 'none'
                          }}
                        >
                          {currentFaucetConfig.enabled ? '⏸️ Disable' : '▶️ Enable'}
                        </button>
                        <button
                          type="button"
                          onClick={handleDeleteFaucetConfig}
                          className="rainbowBtn"
                          style={{ 
                            fontSize: '0.8rem', 
                            padding: '0.4rem 0.8rem', 
                            backgroundColor: '#e74c3c',
                            backgroundImage: 'none'
                          }}
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', fontSize: '0.9rem' }}>
                      <div>
                        <strong style={{ color: '#00bfff' }}>Status:</strong>
                        <div style={{ color: currentFaucetConfig.enabled ? '#22c55e' : '#ef4444', fontWeight: 'bold' }}>
                          {currentFaucetConfig.enabled ? '✅ Enabled' : '❌ Disabled'}
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: '#00bfff' }}>Amount:</strong>
                        <div style={{ color: 'white' }}>{currentFaucetConfig.amount_pol} POL</div>
                      </div>
                      <div>
                        <strong style={{ color: '#00bfff' }}>Frequency:</strong>
                        <div style={{ color: 'white' }}>
                          {currentFaucetConfig.frequency_hours === 168 ? 'Weekly' :
                           currentFaucetConfig.frequency_hours === 336 ? 'Bi-weekly' :
                           currentFaucetConfig.frequency_hours === 720 ? 'Monthly' :
                           currentFaucetConfig.frequency_hours === 2160 ? 'Quarterly' : 
                           `${currentFaucetConfig.frequency_hours}h`}
                        </div>
                      </div>
                      <div>
                        <strong style={{ color: '#00bfff' }}>Token Gates:</strong>
                        <div style={{ color: 'white' }}>
                          {currentFaucetConfig.token_gate_requirements?.length > 0 
                            ? `${currentFaucetConfig.token_gate_requirements.length} collection(s) (${currentFaucetConfig.token_gate_logic})` 
                            : 'Open access'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <h4 style={{ color: 'white', margin: '1.5rem 0 0.5rem 0', borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: '1rem' }}>
                  ⚙️ Create New Configuration
                </h4>
                
                <div className="admin-form-group">
                  <label>Faucet Enabled:</label>
                  <input
                    type="checkbox"
                    checked={faucetConfig.enabled}
                    onChange={(e) => handleFaucetConfigChange('enabled', e.target.checked)}
                    style={{ marginLeft: '0.5rem' }}
                  />
                </div>

                <div className="admin-form-group">
                  <label>Amount of POL per claim:</label>
                  <input
                    type="number"
                    step="0.001"
                    min="0"
                    value={faucetConfig.amount_pol}
                    onChange={(e) => handleFaucetConfigChange('amount_pol', e.target.value)}
                    className="admin-input"
                  />
                  <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                    Recommended: 0.01-0.05 POL (enough for 2-3 transactions)
                  </small>
                </div>

                <div className="admin-form-group">
                  <label>Claim Frequency:</label>
                  <select
                    value={faucetConfig.frequency_hours}
                    onChange={(e) => handleFaucetConfigChange('frequency_hours', parseInt(e.target.value))}
                    className="admin-select"
                  >
                    <option value={168}>Weekly (7 days)</option>
                    <option value={336}>Bi-weekly (14 days)</option>
                    <option value={720}>Monthly (30 days)</option>
                    <option value={2160}>Quarterly (90 days)</option>
                  </select>
                </div>

                {/* Per-Token Limits Section */}
                <div className="admin-form-group" style={{ border: '1px solid #444', borderRadius: '8px', padding: '1rem', marginTop: '1rem' }}>
                  <h4 style={{ color: '#fff', marginTop: 0, marginBottom: '1rem' }}>🔒 Per-Token Limits</h4>
                  
                  <div className="admin-form-group">
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <input
                        type="checkbox"
                        checked={faucetConfig.limit_per_token_enabled}
                        onChange={e =>
                          handleFaucetConfigChange('limit_per_token_enabled', e.target.checked)
                        }
                      />
                      Enable Per-Token Limits
                    </label>
                    <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                      Prevent the same NFT from being used multiple times (anti-transfer exploit)
                    </small>
                  </div>

                  {faucetConfig.limit_per_token_enabled && (
                    <div className="admin-form-group">
                      <label>Uses Per Token:</label>
                      <input
                        type="number"
                        value={faucetConfig.limit_per_token}
                        onChange={e =>
                          handleFaucetConfigChange('limit_per_token', parseInt(e.target.value) || 1)
                        }
                        className="admin-input"
                        placeholder="1"
                        min="1"
                        max="100"
                      />
                      <small style={{ color: '#888', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                        How many times each individual NFT can be used for faucet claims
                      </small>
                    </div>
                  )}
                </div>

                <div className="admin-form-group">
                  <label>Token Gate Logic:</label>
                  <select
                    value={faucetConfig.token_gate_logic}
                    onChange={(e) => handleFaucetConfigChange('token_gate_logic', e.target.value)}
                    className="admin-select"
                  >
                    <option value="AND">Must own ALL required collections (AND)</option>
                    <option value="OR">Must own ANY ONE required collection (OR)</option>
                  </select>
                </div>

                <h4 style={{ color: 'white', margin: '1.5rem 0 0.5rem 0' }}>Token Gate Requirements</h4>
                <p style={{ fontSize: '0.9rem', color: '#888', marginBottom: '1rem' }}>
                  Set which NFT collections users must own to access the faucet. Leave empty for open access.
                </p>

                {faucetConfig.token_gate_requirements.map((gate, gateIndex) => (
                  <div key={gateIndex} className="admin-recipe-item">
                    <div className="admin-form-group">
                      <label>Collection:</label>
                      <select
                        value={gate.collection || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleFaucetTokenGateChange(gateIndex, 'collection', value);
                        }}
                        className="admin-select"
                      >
                        <option value="">– pick collection item –</option>
                        {collections.map(c => (
                          <option key={c.id} value={c.collection_address}>
                            {c.title} (Blockchain Token #{c.token_id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="admin-form-group">
                      <label>Item ID (optional):</label>
                      <input
                        type="text"
                        value={gate.item_id}
                        onChange={(e) => handleFaucetTokenGateChange(gateIndex, 'item_id', e.target.value)}
                        placeholder="Specific token ID (leave empty for any)"
                        className="admin-input"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Quantity:</label>
                      <input
                        type="number"
                        min="1"
                        value={gate.quantity}
                        onChange={(e) => handleFaucetTokenGateChange(gateIndex, 'quantity', parseInt(e.target.value) || 1)}
                        className="admin-input"
                      />
                    </div>
                    <div className="admin-form-group">
                      <label>Marketplace Link (optional):</label>
                      <input
                        type="url"
                        value={gate.marketplace_link || ''}
                        onChange={(e) => handleFaucetTokenGateChange(gateIndex, 'marketplace_link', e.target.value)}
                        placeholder="https://opensea.io/collection/..."
                        className="admin-input"
                      />
                    </div>
                    {faucetConfig.token_gate_requirements.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeFaucetTokenGate(gateIndex)}
                        className="rainbowBtn"
                        style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', backgroundColor: '#e74c3c' }}
                      >
                        Remove Gate
                      </button>
                    )}
                  </div>
                ))}

                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'center' }}>
                  <button
                    type="button"
                    onClick={addFaucetTokenGate}
                    className="rainbowBtn"
                    style={{ backgroundColor: '#8a2be2' }}
                  >
                    Add Token Gate
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmitFaucetConfig}
                    className="rainbowBtn"
                    style={{ 
                      background: 'linear-gradient(45deg, #22c55e, #16a34a)',
                      fontWeight: 'bold'
                    }}
                  >
                    🚀 Deploy New Configuration
                  </button>
                </div>

                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(34, 197, 94, 0.3)' }}>
                  <div style={{ color: '#22c55e', fontSize: '0.9rem', fontWeight: 'bold', marginBottom: '0.25rem' }}>
                    💡 How it works:
                  </div>
                  <div style={{ color: '#888', fontSize: '0.8rem' }}>
                    • Creating a new configuration will automatically replace any existing one<br/>
                    • Only one faucet configuration can be active at a time<br/>
                    • Use "Disable Faucet" above to turn off the faucet completely
                  </div>
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: 'rgba(138, 43, 226, 0.1)', borderRadius: '0.5rem', border: '1px solid rgba(138, 43, 226, 0.3)' }}>
                  <h4 style={{ color: '#8a2be2', margin: '0 0 0.5rem 0' }}>💡 Faucet Tips</h4>
                  <ul style={{ color: '#888', fontSize: '0.9rem', margin: 0, paddingLeft: '1.5rem' }}>
                    <li>Start with a tight frequency (monthly) to control costs</li>
                    <li>Use token gates to reward your VIP NFT holders</li>
                    <li>Monitor the minter wallet balance regularly</li>
                    <li>Amount should cover 2-3 transactions on Polygon (~0.01-0.05 POL)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Toggle for 🔥 Burn Sniffer Dashboard */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowBurnSnifferSection(!showBurnSnifferSection)}
          >
            {showBurnSnifferSection ? '▼' : '►'} 🔥 Burn Sniffer Dashboard
          </h2>
          {showBurnSnifferSection && (
            <div className="glassmorphism-card">
              <BurnSnifferDashboard jwtToken={jwtToken} />
            </div>
          )}
        </div>

        {/* Toggle for 🚨 Upgrade Failures Dashboard */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowUpgradeFailuresSection(!showUpgradeFailuresSection)}
          >
            {showUpgradeFailuresSection ? '▼' : '►'} 🚨 Upgrade Failures Dashboard
          </h2>
          {showUpgradeFailuresSection && (
            <div className="glassmorphism-card">
              <UpgradeFailuresDashboard jwtToken={jwtToken} />
            </div>
          )}
        </div>

        {/* Add Maintenance Mode Section */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowMaintenanceSection(!showMaintenanceSection)}
          >
            {showMaintenanceSection ? '▼' : '►'} 🛠️ Maintenance Mode
          </h2>
          {showMaintenanceSection && (
            <div className="glassmorphism-card">
              <div className="admin-form">
                <div className="admin-form-group">
                  <label>Maintenance Mode:</label>
                  <input
                    type="checkbox"
                    checked={maintenanceMode}
                    onChange={(e) => handleMaintenanceToggle(e.target.checked)}
                    style={{ marginLeft: '0.5rem' }}
                  />
                </div>

                <div className="admin-form-group">
                  <label>Maintenance Message:</label>
                  <textarea
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    onBlur={() => handleMaintenanceMessageChange(maintenanceMessage)}
                    className="admin-input"
                    style={{
                      width: '100%',
                      minHeight: '100px',
                      marginTop: '0.5rem',
                      resize: 'vertical'
                    }}
                    placeholder="Enter maintenance message..."
                  />
                </div>

                <div className="admin-form-group">
                  <label>Admin Bypass PIN:</label>
                  <input
                    type="text"
                    value={bypassPin}
                    onChange={(e) => setBypassPin(e.target.value)}
                    onBlur={() => handleMaintenanceMessageChange(maintenanceMessage)}
                    className="admin-input"
                    style={{
                      width: '200px',
                      marginTop: '0.5rem'
                    }}
                    placeholder="Enter PIN..."
                    maxLength="20"
                  />
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    marginTop: '0.25rem'
                  }}>
                    This PIN allows admins to bypass maintenance mode and access the dashboard.
                  </div>
                </div>

                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '0.5rem'
                }}>
                  <p style={{ color: '#ffc107', margin: 0 }}>
                    ⚠️ When maintenance mode is enabled, users will see this message instead of the connect wallet button.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Add Mint Launching Mode Section */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowMintLaunchingSection(!showMintLaunchingSection)}
          >
            {showMintLaunchingSection ? '▼' : '►'} 🚀 Mint Launching Mode
          </h2>
          {showMintLaunchingSection && (
            <div className="glassmorphism-card">
              <div className="admin-form">
                <div style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  background: 'rgba(0, 191, 255, 0.1)',
                  border: '1px solid rgba(0, 191, 255, 0.3)',
                  borderRadius: '0.5rem'
                }}>
                  <p style={{ color: '#00bfff', margin: 0, fontSize: '0.9rem' }}>
                    ℹ️ This is a second-tier maintenance mode. It only shows when regular maintenance mode is OFF. 
                    When enabled, users see a countdown and mint showcase instead of the normal site.
                  </p>
                </div>

                <div className="admin-form-group">
                  <label>Mint Launching Mode:</label>
                  <select
                    value={mintLaunchingMode ? 'enabled' : 'disabled'}
                    onChange={(e) => setMintLaunchingMode(e.target.value === 'enabled')}
                    className="admin-input"
                    style={{
                      width: '200px',
                      marginTop: '0.5rem'
                    }}
                  >
                    <option value="disabled">DISABLED</option>
                    <option value="enabled">ENABLED</option>
                  </select>
                </div>

                <div className="admin-form-group">
                  <label>Launch Date & Time (Los Angeles Time):</label>
                  <input
                    type="datetime-local"
                    value={mintLaunchDateTime}
                    onChange={(e) => setMintLaunchDateTime(e.target.value)}
                    className="admin-input"
                    style={{
                      width: '300px',
                      marginTop: '0.5rem'
                    }}
                  />
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    marginTop: '0.25rem'
                  }}>
                    The mode will automatically disable when this time is reached.
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>Page Title:</label>
                  <input
                    type="text"
                    value={mintLaunchTitle}
                    onChange={(e) => setMintLaunchTitle(e.target.value)}
                    className="admin-input"
                    style={{
                      width: '100%',
                      marginTop: '0.5rem'
                    }}
                    placeholder="Mint Launching Shortly"
                  />
                </div>

                <div className="admin-form-group">
                  <label>Page Subtitle (Optional):</label>
                  <input
                    type="text"
                    value={mintLaunchSubtitle}
                    onChange={(e) => setMintLaunchSubtitle(e.target.value)}
                    className="admin-input"
                    style={{
                      width: '100%',
                      marginTop: '0.5rem'
                    }}
                    placeholder="Get ready for an exclusive mint experience"
                  />
                </div>

                <div className="admin-form-group">
                  <label>Select Deals to Showcase:</label>
                  <div style={{
                    marginTop: '0.5rem',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    {availableDeals.length === 0 ? (
                      <p style={{ color: '#888', margin: 0 }}>No active deals available</p>
                    ) : (
                      availableDeals.map(deal => (
                        <div key={deal.id} style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0.5rem',
                          marginBottom: '0.5rem',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '0.25rem',
                          border: selectedMintDeals.includes(deal.id) ? '1px solid #00bfff' : '1px solid transparent'
                        }}>
                          <input
                            type="checkbox"
                            checked={selectedMintDeals.includes(deal.id)}
                            onChange={(e) => handleDealSelection(deal.id, e.target.checked)}
                            style={{ marginRight: '0.75rem' }}
                          />
                          {deal.deal_image && (
                            <img 
                              src={deal.deal_image} 
                              alt={deal.deal_name}
                              style={{
                                width: '40px',
                                height: '40px',
                                objectFit: 'cover',
                                borderRadius: '0.25rem',
                                marginRight: '0.75rem'
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 'bold', color: 'white' }}>
                              {deal.deal_name}
                            </div>
                            <div style={{ fontSize: '0.8rem', color: '#888' }}>
                              {deal.deal_type === 'dcl_wearable' ? (
                                <>
                                  {deal.dcl_price_mana > 0 ? `${deal.dcl_price_mana} MANA` : 'Free Mint'} • 
                                  Wallet Limit: {deal.limit_per_wallet || 'Unlimited'}
                                  {deal.limit_per_token_enabled && ` • Token Limit: ${deal.limit_per_token || 1}`}
                                </>
                              ) : (
                                `Traditional Deal • Wallet Limit: ${deal.limit_per_wallet || 'Unlimited'}`
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    marginTop: '0.5rem'
                  }}>
                    Selected deals will be showcased on the mint launching page with their images, costs, and limits.
                  </div>
                </div>

                <div className="admin-form-group">
                  <label>Select Recipes to Showcase:</label>
                  <div style={{
                    marginTop: '0.5rem',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    border: '1px solid rgba(0, 255, 128, 0.2)',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    {availableRecipes.length === 0 ? (
                      <p style={{ color: '#888', margin: 0 }}>No recipes available</p>
                    ) : (
                      availableRecipes.map(recipe => {
                        // Get the first reward for display info
                        const firstReward = recipe.rewards?.[0];
                        const isSelected = selectedMintRecipes.includes(recipe.id);
                        
                        return (
                          <div key={recipe.id} style={{
                            marginBottom: '1rem',
                            border: isSelected ? '1px solid #00ff80' : '1px solid rgba(255, 255, 255, 0.1)',
                            borderRadius: '0.5rem',
                            padding: '1rem',
                            background: isSelected ? 'rgba(0, 255, 128, 0.05)' : 'rgba(255, 255, 255, 0.02)'
                          }}>
                            {/* Recipe Selection Header */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              marginBottom: isSelected ? '1rem' : 0
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => handleRecipeSelection(recipe.id, e.target.checked)}
                                style={{ marginRight: '0.75rem' }}
                              />
                              {firstReward?.image && (
                                <img 
                                  src={firstReward.image} 
                                  alt={recipe.title}
                                  style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '0.25rem',
                                    marginRight: '0.75rem'
                                  }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: 'bold', color: '#00ff80' }}>
                                  {recipe.title}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#888' }}>
                                  {recipe.recipe_type === 'free_mint' ? 'Free Mint' : 'Recipe Burn'} • 
                                  Wallet Limit: {recipe.limit_per_wallet || 'Unlimited'}
                                  {recipe.limit_per_token_enabled && ` • Token Limit: ${recipe.limit_per_token || 2}`}
                                </div>
                              </div>
                            </div>

                            {/* Deal Assignment (only show if recipe is selected) */}
                            {isSelected && selectedMintDeals.length > 0 && (
                              <div style={{ paddingLeft: '3rem' }}>
                                <div style={{ 
                                  fontSize: '0.9rem', 
                                  fontWeight: 'bold', 
                                  color: '#00bfff', 
                                  marginBottom: '0.5rem' 
                                }}>
                                  Show under which deals? (leave unchecked to show standalone)
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                  {selectedMintDeals.map(dealId => {
                                    const deal = availableDeals.find(d => d.id === dealId);
                                    if (!deal) return null;
                                    
                                    const currentMapping = recipeToDealsMapping[recipe.id] || [];
                                    const isLinkedToDeal = currentMapping.includes(dealId);
                                    
                                    return (
                                      <label key={dealId} style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        fontSize: '0.85rem',
                                        color: '#ccc'
                                      }}>
                                        <input
                                          type="checkbox"
                                          checked={isLinkedToDeal}
                                          onChange={(e) => {
                                            const newMapping = { ...recipeToDealsMapping };
                                            if (!newMapping[recipe.id]) newMapping[recipe.id] = [];
                                            
                                            if (e.target.checked) {
                                              if (!newMapping[recipe.id].includes(dealId)) {
                                                newMapping[recipe.id].push(dealId);
                                              }
                                            } else {
                                              newMapping[recipe.id] = newMapping[recipe.id].filter(id => id !== dealId);
                                            }
                                            
                                            setRecipeToDealsMapping(newMapping);
                                          }}
                                          style={{ marginRight: '0.5rem' }}
                                        />
                                        {deal.deal_name}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#888',
                    marginTop: '0.5rem'
                  }}>
                    Selected recipes will appear on the mint launching page as either "Free Claims" (under specific deals) or standalone recipe cards.
                  </div>
                </div>

                {/* Save Button */}
                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <button
                    type="button"
                    onClick={handleSaveMintLaunchingConfig}
                    className="rainbowBtn"
                    style={{ 
                      fontSize: '1rem',
                      padding: '0.75rem 2rem'
                    }}
                  >
                    Save Mint Launching Configuration
                  </button>
                </div>

                <div style={{
                  marginTop: '1rem',
                  padding: '1rem',
                  background: 'rgba(255, 193, 7, 0.1)',
                  border: '1px solid rgba(255, 193, 7, 0.3)',
                  borderRadius: '0.5rem'
                }}>
                  <p style={{ color: '#ffc107', margin: 0 }}>
                    ⚠️ Hierarchy: Regular Maintenance Mode (if enabled) always takes priority over Mint Launching Mode.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Nadscription Campaigns Management Section */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowNadscriptionCampaignsSection(!showNadscriptionCampaignsSection)}
          >
            {showNadscriptionCampaignsSection ? '▼' : '►'} 💀 Nadscription Campaigns
          </h2>
          {showNadscriptionCampaignsSection && (
            <div className="glassmorphism-card">
              <NadscriptionCampaigns jwtToken={jwtToken} />
            </div>
          )}
        </div>

        {/* Nadscription Collection Export Section */}
        <div className="admin-section">
          <h2
            className="admin-subheader"
            onClick={() => setShowNadscriptionExportSection(!showNadscriptionExportSection)}
          >
            {showNadscriptionExportSection ? '▼' : '►'} 📦 Nadscription Export & Analytics
          </h2>
          {showNadscriptionExportSection && (
            <div className="glassmorphism-card">
              <NadscriptionExport jwtToken={jwtToken} />
            </div>
          )}
        </div>

        {message && <div className="admin-message">{message}</div>}

        {/* Modal for unsaved mapping removal */}
        {showDeleteModal && (
          <UpgradeModal
            selectedNFTs={[{ id: mintToDeleteIndex }]}
            onClose={() => setShowDeleteModal(false)}
            onConfirm={confirmRemoveMintEntry}
            confirmTitle="Remove Mint Mapping"
            confirmMessage="Are you sure you want to remove this mint mapping?"
            confirmButtonText="YES, DELETE IT"
            cancelButtonText="NO"
            danger={true}
          />
        )}

        {/* Modal for deleting an *existing* deal */}
        {showDeleteDealModal && (
          <UpgradeModal
            selectedNFTs={[{ id: dealToDeleteId }]}
            onClose={() => setShowDeleteDealModal(false)}
            onConfirm={() => {
              handleDeleteExistingDeal(dealToDeleteId);
              setShowDeleteDealModal(false);
              setDealToDeleteId(null);
            }}
            confirmTitle="Remove Deal"
            confirmMessage="Are you sure you want to remove this deal?"
            confirmButtonText="YES, DELETE IT"
            cancelButtonText="NO"
            danger={true}
          />
        )}

        {/* Modal for existing mapping removal */}
        {showDeleteExistingModal && (
          <UpgradeModal
            selectedNFTs={[{ id: mappingToDeleteId }]}
            onClose={() => setShowDeleteExistingModal(false)}
            onConfirm={async () => {
              await handleDeleteExistingMapping(mappingToDeleteId);
              setShowDeleteExistingModal(false);
              setMappingToDeleteId(null);
            }}
            confirmTitle="Remove Mint Mapping"
            confirmMessage="Are you sure you want to remove this mint mapping?"
            confirmButtonText="YES, DELETE IT"
            cancelButtonText="NO"
            danger={true}
          />
        )}

        {/* Modal for DCL deal removal */}
        {showDeleteDCLDealModal && (
          <UpgradeModal
            selectedNFTs={[{ id: dclDealToDeleteId }]}
            onClose={() => setShowDeleteDCLDealModal(false)}
            onConfirm={() => {
              handleDeleteExistingDCLDeal(dclDealToDeleteId);
              setShowDeleteDCLDealModal(false);
              setDclDealToDeleteId(null);
            }}
            confirmTitle="Remove DCL Wearable Deal"
            confirmMessage="Are you sure you want to remove this DCL wearable deal?"
            confirmButtonText="YES, DELETE IT"
            cancelButtonText="NO"
            danger={true}
          />
        )}

        {/* RNG Test Modal */}
        {showRNGTestModal && rngTestItem && (
          <RNGTestModal
            item={rngTestItem}
            itemType={rngTestItemType}
            onClose={() => {
              setShowRNGTestModal(false);
              setRngTestItem(null);
              setRngTestItemType(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default AdminDashboard;
